import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { api } from '../../api/api';
import { API_ORIGIN } from '../../api/client';
import {
  CopyIcon,
  DownloadIcon,
  CheckIcon,
  NavButtons,
  PreviewToggle,
} from '../common';
import { systemFonts } from '../../utils/constants';
import { readSelectedEventScope } from '../../utils/eventScopeStorage';

const escapeHtml = value => {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const escapeAttribute = value => {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const resolveLocalizedText = (baseValue, translations, language) => {
  if (!translations || typeof translations !== 'object') {
    return baseValue;
  }
  const keys = Object.keys(translations);
  const englishKey = keys.find(key => String(key).toLowerCase() === 'en');
  const englishValue =
    englishKey && translations[englishKey] ? translations[englishKey] : null;

  if (!language) {
    return englishValue || baseValue;
  }

  const normalized = String(language).toLowerCase();
  const directKey = keys.find(key => String(key).toLowerCase() === normalized);
  if (directKey && translations[directKey]) return translations[directKey];
  const baseCode = normalized.split('-')[0];
  if (!baseCode) return englishValue || baseValue;
  const baseKey = keys.find(key => String(key).toLowerCase() === baseCode);
  if (baseKey && translations[baseKey]) return translations[baseKey];
  return englishValue || baseValue;
};
const SCRIPTLET_REGEX = /<%[\s\S]*?%>/g;
const GROOVY_EXPR_REGEX = /\$\{[\s\S]*?\}/g;

const protectScriptlets = text => {
  const placeholders = [];
  let safeText = String(text || '');
  safeText = safeText.replace(SCRIPTLET_REGEX, match => {
    const token = `@@SCRIPTLET_${placeholders.length}@@`;
    placeholders.push({ token, value: match });
    return token;
  });
  safeText = safeText.replace(GROOVY_EXPR_REGEX, match => {
    const token = `@@GROOVY_EXPR_${placeholders.length}@@`;
    placeholders.push({ token, value: match });
    return token;
  });
  return { safeText, placeholders };
};

const restoreScriptlets = (text, placeholders) =>
  (placeholders || []).reduce(
    (acc, item) => acc.split(item.token).join(item.value),
    String(text || '')
  );

const LEGACY_FOOTER_PLACEHOLDER = '© EVENT 2026 - All rights reserved.';
const DEFAULT_CONFIDENTIALITY_NOTE =
  'Confidentiality Note: This message is intended only for the use of the named recipient(s) and may contain confidential and/or privileged information. If you are not the intended recipient, please contact the sender and delete the message. Any unauthorized use of the information contained in this message is prohibited.';

const resolveFooterBaseText = settings => {
  const englishFooter =
    settings?.footerTextTranslations &&
    typeof settings.footerTextTranslations === 'object' &&
    typeof settings.footerTextTranslations.en === 'string' &&
    settings.footerTextTranslations.en.trim()
      ? settings.footerTextTranslations.en
      : null;

  if (settings?.footerText === undefined || settings?.footerText === null) {
    return englishFooter || LEGACY_FOOTER_PLACEHOLDER;
  }

  if (settings.footerText === LEGACY_FOOTER_PLACEHOLDER && englishFooter) {
    return englishFooter;
  }

  return settings.footerText;
};

const SECTION_MARKER_REGEX = /\[\[SECTION:\s*([^\]]+)\s*\]\]/gi;
const CTA_MARKER_REGEX = /\[\[CTA:\s*([^\]]+)\s*\]\]/gi;
const ACTION_BLOCK_MARKER_REGEX =
  /\[\[\s*(WALLET|CALENDAR|CONGRESS_APP_LINKS|CONGRESS_APPS)\s*\]\]/gi;
const SECTION_GUARD_OPEN_REGEX = /^\s*(<%\s*if\s*\([\s\S]*?\)\s*\{\s*%>)/;
const SECTION_GUARD_CLOSE_REGEX = /(<%\s*\}\s*%>)\s*$/;

const extractSectionGuard = raw => {
  const source = typeof raw === 'string' ? raw : '';
  const openMatch = source.match(SECTION_GUARD_OPEN_REGEX);
  const closeMatch = source.match(SECTION_GUARD_CLOSE_REGEX);
  if (!openMatch || !closeMatch) {
    return { guardOpen: '', guardClose: '', body: source };
  }
  const openToken = openMatch[1] || '';
  const closeToken = closeMatch[1] || '';
  const bodyStart = source.indexOf(openToken) + openToken.length;
  const bodyEnd = source.lastIndexOf(closeToken);
  if (bodyStart < 0 || bodyEnd < bodyStart) {
    return { guardOpen: '', guardClose: '', body: source };
  }
  return {
    guardOpen: openToken,
    guardClose: closeToken,
    body: source.slice(bodyStart, bodyEnd).trim(),
  };
};


const createOutlookSpacer = height => {
  const safeHeight = Number.isFinite(Number(height)) ? Number(height) : 16;
  return `<!--[if mso | IE]><table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%"><tr><td style="line-height:${safeHeight}px;height:${safeHeight}px;">&nbsp;</td></tr></table><![endif]-->`;
};
const DIVIDER_GAP_PX = 24;

const formatDateForCalendar = input => {
  if (!input) return null;
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '') + 'Z';
};

const sanitizeForICS = value =>
  (value || '')
    .replace(/\\/g, '\\\\')
    .replace(/\r?\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');

const sanitizeFileName = value => {
  const fallback = 'event.ics';
  if (!value || typeof value !== 'string') return fallback;
  return value.trim().replace(/[^a-zA-Z0-9._-]/g, '_') || fallback;
};

const normalizeWebsiteHref = url => {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const deriveWebsiteLinkText = (displayText, url) => {
  if (displayText && typeof displayText === 'string' && displayText.trim()) {
    return displayText.trim();
  }
  if (!url || typeof url !== 'string') return '';
  return url.trim().replace(/^\s*https?:\/\//i, '');
};

const calendarProviderSvgMap = {
  apple:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="#ffffff" d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/></svg>',
  google:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="#ffffff" d="M18.316 5.684H24v12.632h-5.684V5.684zM5.684 24h12.632v-5.684H5.684V24zM18.316 5.684V0H1.895A1.894 1.894 0 0 0 0 1.895v16.421h5.684V5.684h12.632zm-7.207 6.25v-.065c.272-.144.5-.349.687-.617s.279-.595.279-.982c0-.379-.099-.72-.3-1.025a2.05 2.05 0 0 0-.832-.714 2.703 2.703 0 0 0-1.197-.257c-.6 0-1.094.156-1.481.467-.386.311-.65.671-.793 1.078l1.085.452c.086-.249.224-.461.413-.633.189-.172.445-.257.767-.257.33 0 .602.088.816.264a.86.86 0 0 1 .322.703c0 .33-.12.589-.36.778-.24.19-.535.284-.886.284h-.567v1.085h.633c.407 0 .748.109 1.02.327.272.218.407.499.407.843 0 .336-.129.614-.387.832s-.565.327-.924.327c-.351 0-.651-.103-.897-.311-.248-.208-.422-.502-.521-.881l-1.096.452c.178.616.505 1.082.977 1.401.472.319.984.478 1.538.477a2.84 2.84 0 0 0 1.293-.291c.382-.193.684-.458.902-.794.218-.336.327-.72.327-1.149 0-.429-.115-.797-.344-1.105a2.067 2.067 0 0 0-.881-.689zm2.093-1.931l.602.913L15 10.045v5.744h1.187V8.446h-.827l-2.158 1.557zM22.105 0h-3.289v5.184H24V1.895A1.894 1.894 0 0 0 22.105 0zm-3.289 23.5l4.684-4.684h-4.684V23.5zM0 22.105C0 23.152.848 24 1.895 24h3.289v-5.184H0v3.289z"/></svg>',
  outlook:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="#ffffff" d="M3.5 5.25A2.25 2.25 0 0 1 5.75 3h12.5A2.75 2.75 0 0 1 21 5.75V9l-9 5.625L3 9.01v5.24A2.75 2.75 0 0 0 5.75 17h12.5A2.75 2.75 0 0 0 21 14.25V21h-2.5A2.5 2.5 0 0 1 16 18.5v-.257l-2.372 1.482c-.856.535-1.934.535-2.79 0L3 15.75V5.25zM9.5 10a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zm0 1.5a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/></svg>',
  yahoo:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><text x="12" y="15" fill="#ffffff" font-size="14" font-family="Arial, sans-serif" font-weight="700" text-anchor="middle">Y!</text></svg>',
};

const getCalendarProviderLogo = key => {
  const svg = calendarProviderSvgMap[key];
  if (!svg) return '';
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

const LAYOUT_VARIANTS = {
  DEFAULT: 'default',
  REGMAIL: 'regmail',
};

const resolveLayoutVariant = settings =>
  `${settings?.regmailEnabled ? LAYOUT_VARIANTS.REGMAIL : LAYOUT_VARIANTS.DEFAULT}|${
    settings?.includeWalletButtons ? 'wallet-on' : 'wallet-off'
  }|${settings?.includeCalendarButton ? 'calendar-on' : 'calendar-off'}`;

const selectVisualVariant = (visualEditor, layoutKey) => {
  if (!visualEditor || typeof visualEditor !== 'object') {
    return null;
  }
  if (visualEditor.variants && typeof visualEditor.variants === 'object') {
    if (visualEditor.variants[layoutKey]) {
      return visualEditor.variants[layoutKey];
    }
    const parts = layoutKey.split('|');
    while (parts.length > 1) {
      parts.pop();
      const fallbackKey = parts.join('|');
      if (visualEditor.variants[fallbackKey]) {
        return visualEditor.variants[fallbackKey];
      }
    }
    return null;
  }
  return visualEditor;
};

const composeVisualDocument = (visualHtml, fallbackHtml) => {
  if (!visualHtml || typeof visualHtml !== 'string') return fallbackHtml;
  if (/<!doctype/i.test(visualHtml) || /<html/i.test(visualHtml)) {
    return visualHtml;
  }
  if (!fallbackHtml || typeof fallbackHtml !== 'string') {
    return visualHtml;
  }
  const bodyOpenIdx = fallbackHtml.toLowerCase().indexOf('<body');
  if (bodyOpenIdx === -1) {
    return visualHtml;
  }
  const bodyStart = fallbackHtml.indexOf('>', bodyOpenIdx);
  if (bodyStart === -1) {
    return visualHtml;
  }
  const bodyCloseIdx = fallbackHtml.toLowerCase().lastIndexOf('</body>');
  if (bodyCloseIdx === -1) {
    return visualHtml;
  }
  const docStart = fallbackHtml.slice(0, bodyStart + 1);
  const docEnd = fallbackHtml.slice(bodyCloseIdx);
  const trimmed = visualHtml.replace(/^\s*<style[\s\S]*?<\/style>/i, '').trim();
  if (!trimmed) {
    return fallbackHtml;
  }
  return `${docStart}\n${trimmed}\n${docEnd}`;
};

export function getGoogleFontsImport(fontFamily) {
  if (!fontFamily || systemFonts.has(fontFamily)) return '';
  const fontQuery = fontFamily.replace(/ /g, '+');
  return `
        <link href="https://fonts.googleapis.com/css2?family=${fontQuery}:wght@400;700&display=swap" rel="stylesheet">
        <style type="text/css">
            @import url('https://fonts.googleapis.com/css2?family=${fontQuery}:wght@400;700&display=swap');
        </style>
    `;
}

// Extracted pure function for generating email HTML
export function generateEmailHtml(settings, globalSettings, options = {}) {
  const baseContainerRadius = Math.max(0, Number(settings.tableBorderRadius) || 0);
  const { ignoreVisualEditor = false } = options || {};
  const layoutVariant = resolveLayoutVariant(settings);
  const visualState = !ignoreVisualEditor
    ? selectVisualVariant(settings?.visualEditor, layoutVariant)
    : null;

  const resolveVisualHtml = () => {
    if (!visualState || typeof visualState !== 'object') return null;
    if (typeof visualState.html === 'string' && visualState.html.trim()) {
      return visualState.html;
    }
    const raw = typeof visualState.rawHtml === 'string' ? visualState.rawHtml.trim() : '';
    if (!raw) return null;
    const cssBlock =
      typeof visualState.css === 'string' && visualState.css.trim()
        ? `<style>${visualState.css}</style>`
        : '';
    return `${cssBlock}${raw}`;
  };

  const visualHtml = ignoreVisualEditor ? null : resolveVisualHtml();

  if (!ignoreVisualEditor && typeof visualHtml === 'string' && visualHtml.trim()) {
    const fallbackHtml = generateEmailHtml(settings, globalSettings, {
      ignoreVisualEditor: true,
    });
    return composeVisualDocument(visualHtml, fallbackHtml);
  }
  if (!settings || !globalSettings) {
    return '';
  }
  // Flag for Regmail layout
  const resolveExplicitRegmailFlag = source =>
    source &&
    typeof source === 'object' &&
    Object.prototype.hasOwnProperty.call(source, 'regmailEnabled')
      ? Boolean(source.regmailEnabled)
      : undefined;

  const explicitRegmail = resolveExplicitRegmailFlag(settings);

  const heuristicsRegmail = (() => {
    const templateName =
      settings?.name && typeof settings.name === 'string'
        ? settings.name.toLowerCase()
        : '';
    const categoryName =
      settings?.category && typeof settings.category === 'string'
        ? settings.category.toLowerCase()
        : '';
    if (templateName.includes('regmail') || categoryName.includes('regmail')) {
      return true;
    }

    const regmailKeys = [
      'eventName',
      'hostingOrganisation',
      'eventLocation',
      'eventStartDate',
      'eventEndDate',
      'eventTime',
      'paymentDueDate',
      'eventEmail',
    ];
    const hasKey = key =>
      settings && Object.prototype.hasOwnProperty.call(settings, key);
    if (regmailKeys.some(hasKey)) return true;

    const regmailBlock =
      settings?.regmail && typeof settings.regmail === 'object'
        ? settings.regmail
        : null;
    if (regmailBlock) {
      return regmailKeys.some(key =>
        Object.prototype.hasOwnProperty.call(regmailBlock, key)
      );
    }

    return false;
  })();

  const isRegmail = explicitRegmail ?? heuristicsRegmail;
  // Email client compatible font stack
  const getFontStack = fontFamily => {
    const fallbacks = 'Arial, Helvetica, sans-serif';
    if (!fontFamily) return fallbacks;
    if (systemFonts.has(fontFamily)) return `'${fontFamily}', ${fallbacks}`;
    return `'${fontFamily}', ${fallbacks}`;
  };

  const buildTextStyle = settings =>
    `font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor}; font-family: ${getFontStack(settings.templateFontFamily)};`;

  const renderInlineMarkdown = (raw, settings) => {
    if (!raw) return '';
    const rawText = String(raw);
    const qrTagRegex =
      /<img\b[^>]*src=(["'])https:\/\/kit-react\.de\/regasus\/api\/rest\/v1\/qrcode\?content=\$\{p\.barcode\}\1[^>]*\/?>/gi;
    const qrTagEscapedRegex =
      /&lt;img\b[\s\S]*?qrcode\?content=\$\{p\.barcode\}[\s\S]*?\/?&gt;/gi;
    const qrTokens = [];
    const toQrToken = () => {
      const token = `__QR_CODE_IMAGE_${qrTokens.length}__`;
      qrTokens.push(token);
      return token;
    };
    const textWithRawQrTokens = rawText.replace(qrTagRegex, () =>
      toQrToken()
    );
    const textWithQrTokens = textWithRawQrTokens.replace(
      qrTagEscapedRegex,
      () => toQrToken()
    );
    const { safeText, placeholders } = protectScriptlets(textWithQrTokens);

    let out = escapeHtml(safeText);
    out = out.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, altRaw, url) => {
      const cleanUrl = String(url || '').trim();
      if (!/^https?:\/\//i.test(cleanUrl)) return match;
      const safeUrl = escapeAttribute(cleanUrl);
      const [altPart, widthPart] = String(altRaw || '').split('|');
      const altText = String(altPart || '').trim() || 'Image';
      const parsedWidth = Number.parseInt(String(widthPart || '').trim(), 10);
      const hasWidth = Number.isFinite(parsedWidth) && parsedWidth >= 24 && parsedWidth <= 600;
      const widthAttr = hasWidth ? ` width="${parsedWidth}"` : '';
      const widthStyle = hasWidth ? `${parsedWidth}px` : '100%';
      return `<img src="${safeUrl}" alt="${escapeAttribute(altText)}"${widthAttr} style="display:block;height:auto;max-width:${widthStyle};border:0;">`;
    });
    out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) => {
      const cleanUrl = String(url || '').trim();
      if (!/^(https?:\/\/|mailto:|tel:)/i.test(cleanUrl)) return match;
      const safeUrl = escapeAttribute(cleanUrl);
      return `<a href="${safeUrl}" style="color: ${settings.linkColor}; text-decoration: none;">${text}</a>`;
    });
    out = out.replace(/__([^_]+)__/g, '<u>$1</u>');
    out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    out = out.replace(/\*([^*\n]+)\*/g, '<em>$1</em>');
    qrTokens.forEach(token => {
      out = out.replace(
        token,
        '<img alt="" src="https://kit-react.de/regasus/api/rest/v1/qrcode?content=$' +
          '{p.barcode}" height="120" width="120" style="width:138px;height:138px;" />'
      );
    });
    return restoreScriptlets(out, placeholders);
  };

  const parseInlineCtaMarker = raw => {
    const parsed = { type: 'primary', text: '', url: '' };
    if (!raw || typeof raw !== 'string') return parsed;
    const parts = raw
      .split(',')
      .map(part => part.trim())
      .filter(Boolean);
    parts.forEach(part => {
      const lower = part.toLowerCase();
      if (['primary', 'secondary', 'all'].includes(lower)) {
        parsed.type = lower;
        return;
      }
      const idx = part.indexOf(':');
      if (idx < 0) return;
      const key = part.slice(0, idx).trim().toLowerCase();
      const value = part.slice(idx + 1).trim();
      if (key === 'text') parsed.text = value;
      if (key === 'url') parsed.url = value;
    });
    return parsed;
  };

  const parseTableRow = line => {
    let row = (line || '').trim();
    if (row.startsWith('|')) row = row.slice(1);
    if (row.endsWith('|')) row = row.slice(0, -1);
    return row.split('|').map(cell => cell.trim());
  };

  const isTableSeparator = line =>
    /^\s*\|?\s*:?[-]+:?\s*(\|\s*:?[-]+:?\s*)+\|?\s*$/.test(line || '');

  const renderTable = (headerLine, bodyLines, settings) => {
    const textStyle = buildTextStyle(settings);
    const cellStyle = `border: 1px solid #e5e7eb; padding: 6px 8px; ${textStyle}`;
    const headerCells = parseTableRow(headerLine).map(
      cell =>
        `<th style="${cellStyle} font-weight: 600; text-align: left;">${renderInlineMarkdown(cell, settings)}</th>`
    );
    const bodyRows = bodyLines.map(line => {
      const cells = parseTableRow(line).map(
        cell => `<td style="${cellStyle}">${renderInlineMarkdown(cell, settings)}</td>`
      );
      return `<tr>${cells.join('')}</tr>`;
    });
    return `
            <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="width: 100%; border-collapse: collapse; margin: 8px 0 16px 0;">
              <thead><tr>${headerCells.join('')}</tr></thead>
              <tbody>${bodyRows.join('')}</tbody>
            </table>
          `;
  };

  const formatRichText = (raw, settings) => {
    if (!raw) return '';
    const textStyle = buildTextStyle(settings);
    const standaloneQrRegex =
      /^\s*(?:<img\b[^>]*qrcode\?content=\$\{p\.barcode\}[^>]*\/?>|&lt;img\b[\s\S]*?qrcode\?content=\$\{p\.barcode\}[\s\S]*?\/?&gt;)\s*$/i;
    const lines = String(raw).split(/\r?\n/);
    const blocks = [];
    let paragraph = [];

    const flushParagraph = () => {
      if (!paragraph.length) return;
      const content = paragraph.join('\n');
      if (standaloneQrRegex.test(content.trim())) {
        const html = renderInlineMarkdown(content, settings);
        blocks.push(`<div style="margin: 0 0 12px 0;">${html}</div>`);
      } else {
        const html = renderInlineMarkdown(content, settings).replace(/\n/g, '<br>');
        blocks.push(
          `<p style="${textStyle} margin: 0 0 12px 0;">${html}</p>`
        );
      }
      paragraph = [];
    };

    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      if (!line.trim()) {
        flushParagraph();
        i += 1;
        continue;
      }

      if (line.includes('<%')) {
        flushParagraph();
        const scriptletLines = [line];
        let scriptletClosed = line.includes('%>');
        while (!scriptletClosed && i + 1 < lines.length) {
          i += 1;
          const nextScriptletLine = lines[i];
          scriptletLines.push(nextScriptletLine);
          if (nextScriptletLine.includes('%>')) {
            scriptletClosed = true;
          }
        }
        blocks.push(scriptletLines.join('\n'));
        i += 1;
        continue;
      }

      const nextLine = lines[i + 1];
      if (line.includes('|') && isTableSeparator(nextLine)) {
        flushParagraph();
        const headerLine = line;
        const bodyLines = [];
        i += 2;
        while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
          bodyLines.push(lines[i]);
          i += 1;
        }
        blocks.push(renderTable(headerLine, bodyLines, settings));
        continue;
      }

      const listMatch = line.match(/^\s*[-*]\s+(.*)$/);
      if (listMatch) {
        flushParagraph();
        const items = [];
        while (i < lines.length) {
          const current = lines[i];
          const match = current.match(/^\s*[-*]\s+(.*)$/);
          if (!match) break;
          items.push(match[1]);
          i += 1;
        }
        const listItems = items
          .map(
            item =>
              `<li style="margin: 0 0 6px 0;">${renderInlineMarkdown(item, settings)}</li>`
          )
          .join('');
        blocks.push(
          `<ul style="margin: 0 0 12px 18px; padding: 0; ${textStyle}">${listItems}</ul>`
        );
        continue;
      }

      paragraph.push(line);
      i += 1;
    }
    flushParagraph();
    return blocks.join('');
  };
  // Generate banner HTML
  const getBanners = (location, placement) => {
    const tableMaxWidth = 600;
    return (settings.banners || [])
      .filter(
        b =>
          b.enabled &&
          b.url &&
          b.location === location &&
          b.placement === placement
      )
      .map(banner => {
        const sizeMode = banner.sizeMode || 'auto';
        const hasAutoCandidate = sizeMode === 'auto';
        const fixedHeight = Math.max(1, parseInt(banner.height || 200, 10));
        // Derive aspect ratio percent from explicit value or from height vs. table width
        const derivedPercent =
          typeof banner.aspectRatioPercent === 'number' && banner.aspectRatioPercent > 0
            ? Number(
                banner.aspectRatioPercent.toFixed
                  ? banner.aspectRatioPercent.toFixed(2)
                  : banner.aspectRatioPercent
              )
            : Math.round((fixedHeight / tableMaxWidth) * 100 * 100) / 100; // 2 decimal places
        const hasAuto = hasAutoCandidate && derivedPercent > 0;
        const pb = hasAuto ? derivedPercent : null;
        if (hasAuto) {
          // Responsive hero using padding-bottom technique for modern clients.
          // For Outlook Desktop (MSO), use an <img> tag to avoid any cropping.
          return `
                <tr>
                    <td style="padding: 0;">
                        <!--[if mso]>
                        <img src="${banner.url}" alt="${banner.altText || ''}" width="${tableMaxWidth}" style="width: 100%; height: auto; display: block; border: 0; line-height: 0;" />
                        <![endif]-->
                        <!--[if !mso]><!-- -->
                        <div style="width: 100%; height: 0; padding-bottom: ${pb}%; background: url('${banner.url}') center center / cover no-repeat; line-height: 0; font-size: 0; mso-line-height-rule: exactly;">
                            &nbsp;
                        </div>
                        <!--<![endif]-->
                    </td>
                </tr>`;
        }

        // Fixed height fallback (previous behavior)
        if (isRegmail) {
          return `
                <tr>
                    <td style="padding: 0;">
                        <!--[if mso]>
                        <img src="${banner.url}" alt="${banner.altText || ''}" width="${tableMaxWidth}" style="width: 100%; height: auto; display: block; border: 0;" />
                        <![endif]-->
                        <!--[if !mso]><!-- -->
                        <div style="height: ${fixedHeight}px; background: url('${banner.url}') center center / cover no-repeat; line-height: ${fixedHeight}px; font-size: 0; mso-line-height-rule: exactly;">
                            &nbsp;
                        </div>
                        <!--<![endif]-->
                    </td>
                </tr>`;
        }
        return `
                <tr>
                    <td style="padding: 0;">
                        <!--[if mso]>
                        <img src="${banner.url}" alt="${banner.altText || ''}" width="${tableMaxWidth}" style="width: 100%; height: auto; display: block; border: 0; line-height: 0;" />
                        <![endif]-->
                        <!--[if !mso]><!-- -->
                        <div style="height: ${fixedHeight}px; background: url('${banner.url}') center center / cover no-repeat; line-height: ${fixedHeight}px; font-size: 0; mso-line-height-rule: exactly;">
                            &nbsp;
                        </div>
                        <!--<![endif]-->
                    </td>
                </tr>`;
      })
      .join('');
  };
  // Helper for styled text
  const applyStylesToText = (text, styles) => {
    if (!text) return '';
    if (!styles || styles.includes('normal')) return text;
    let styledText = text;
    if (styles.includes('bold')) styledText = `<strong>${styledText}</strong>`;
    if (styles.includes('italic')) styledText = `<em>${styledText}</em>`;
    if (styles.includes('underline')) styledText = `<u>${styledText}</u>`;
    return styledText;
  };

  // Generate header area HTML
  const generateHeaderAreaHtml = () => {
    if (!settings.includeTopPadding) return '';
    const paddingHeight = settings.topPaddingHeight || 20;
    const bgColor =
      settings.topPaddingBackgroundColor || settings.footerBackgroundColor;
    const textColor = settings.topPaddingTextColor || settings.footerTextColor;
    const paddingText = settings.topPaddingText || '';
    const fontSize = settings.topPaddingFontSize || settings.footerFontSize;

    if (paddingText) {
      return `
                <tr>
                    <td data-kit-top-padding="container" style="padding: ${paddingHeight}px; background-color: ${bgColor}; color: ${textColor}; text-align: center; font-size: ${fontSize}pt; font-family: ${getFontStack(settings.templateFontFamily)}; border-radius: ${settings.footerBorderRadius}px ${settings.footerBorderRadius}px 0 0;">
                        <span data-kit-top-padding="text">${escapeHtml(paddingText)}</span>
                    </td>
                </tr>
            `;
    } else {
      return `
                <tr>
                    <td data-kit-top-padding="container" style="height: ${paddingHeight}px; background-color: ${bgColor};">
                    </td>
                </tr>
            `;
    }
  };

  // Generate footer HTML
  const generateFooterHtml = () => {
    if (settings.includeFooter === false) return '';
    const footerBaseText = resolveFooterBaseText(settings);
    let footerText = resolveLocalizedText(
      footerBaseText,
      settings.footerTextTranslations,
      settings.textTemplateLanguage
    );

    // Default footer placeholder (only when undefined/null; allow empty string)
    if (footerText === undefined || footerText === null) {
      footerText = LEGACY_FOOTER_PLACEHOLDER;
    }

    return `
            <tr data-kit-footer="row">
                <td data-kit-footer="container" style="padding: ${settings.footerPadding}px; background-color: ${settings.footerBackgroundColor}; color: ${settings.footerTextColor}; text-align: center; font-size: ${settings.footerFontSize}pt; font-family: ${getFontStack(settings.templateFontFamily)}; border-radius: 0 0 ${settings.footerBorderRadius}px ${settings.footerBorderRadius}px;">
                    <span data-kit-footer="text">
                        ${applyStylesToText(footerText, settings.footerStyles)}
                    </span>
                </td>
            </tr>
        `;
  };

  const generateConfidentialityNoteHtml = () => {
    if (!settings.includeConfidentialityNote) return '';
    let confidentialityNote =
      resolveLocalizedText(
        settings.confidentialityNote,
        settings.confidentialityNoteTranslations,
        settings.textTemplateLanguage
      ) || '';
    if (!confidentialityNote) {
      confidentialityNote = DEFAULT_CONFIDENTIALITY_NOTE;
    }
    return `
            <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="width: 100%; margin-top: 12px;">
                <tr>
                    <td data-kit-confidentiality-note="container" style="font-size: ${settings.confidentialityNoteFontSize || settings.footerFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: #666666; font-family: ${getFontStack(settings.templateFontFamily)}; text-align: left;">
                        ${applyStylesToText(confidentialityNote, settings.confidentialityNoteStyles || settings.footerStyles)}
                    </td>
                </tr>
            </table>
        `;
  };

  // Generate wallet section HTML
  const generateWalletHtml = () => {
    if (!settings.includeWalletButtons) return '';

    const containerRadius = baseContainerRadius;
    const walletFontFamily = getFontStack(settings.templateFontFamily);
    const allowedAlignments = ['left', 'center', 'right', 'justify'];
    const normalizedAlign = (
      settings.walletTextAlign || settings.walletTextAlignment || ''
    )
      .toString()
      .toLowerCase();
    const walletTextAlign = allowedAlignments.includes(normalizedAlign)
      ? normalizedAlign
      : 'left';
    const walletTextColor =
      settings.walletTextColor || settings.bodyTextColor || '#333333';
    const listMargin =
      walletTextAlign === 'left' ? '0 0 15px 20px' : '0 0 15px 0';
    const listStylePosition =
      walletTextAlign === 'left' ? 'outside' : 'inside';
    const instructions = (settings.walletInstructionsText || '')
      .split('\n')
      .filter(line => line.trim())
      .map(
        line =>
          `<li style="margin-bottom: 6px; color: ${walletTextColor}; font-family: ${walletFontFamily}; text-align: ${walletTextAlign};">${applyStylesToText(line, settings.walletInstructionsStyles)}</li>`
      )
      .join('');

    return `
            <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="width: 100%; margin: 20px 0;">
                <tr>
                    <td style="background-color: ${settings.walletBackgroundColor}; padding: 15px; border-radius: ${containerRadius}px;">
                        <p style="font-size: ${settings.walletIntroFontSize}pt; line-height: ${settings.paragraphLineHeight}; margin: 0 0 15px 0; color: ${walletTextColor}; text-align: ${walletTextAlign}; font-family: ${walletFontFamily};">
                            ${applyStylesToText((settings.walletIntroText || '').replace(/\n/g, '<br>'), settings.walletIntroStyles)}
                        </p>
                        <ol style="font-size: ${settings.walletInstructionsFontSize}pt; line-height: ${settings.paragraphLineHeight}; margin: ${listMargin}; padding: 0; color: ${walletTextColor}; text-align: ${walletTextAlign}; list-style-position: ${listStylePosition}; font-family: ${walletFontFamily};">
                            ${instructions}
                        </ol>
                        <div style="text-align: center;">
                            <a href="https://kit-react.de/regasus/api/rest/v1/applePasses?id=\${p.encryptedIdForUrl}" target="_blank" style="display: inline-block; margin-right: 10px;">
                                <img src="${(globalSettings && globalSettings.imageUrls && globalSettings.imageUrls.appleWallet) || ''}" width="150" alt="Apple Wallet" style="border: 0; display: block;">
                            </a>
                            <a href="\${googlePassLink}" target="_blank" style="display: inline-block;">
                                <img src="${(globalSettings && globalSettings.imageUrls && globalSettings.imageUrls.googleWallet) || ''}" width="171" alt="Google Wallet" style="border: 0; display: block;">
                            </a>
                        </div>
                    </td>
                </tr>
            </table>
        `;
  };

  // Generate CTA buttons HTML
  const renderCtaButtonsBlock = (buttons, placement) => {
    if (!Array.isArray(buttons) || buttons.length === 0) return '';
    return buttons
      .map((btn, index) => {
        const padY = Number(btn.paddingY) || 10;
        const padX = Number(btn.paddingX) || 20;
        const fontSize = Number(btn.fontSize) || 16;
        const resolvedStyles = Array.isArray(btn.styles) ? btn.styles : ['bold'];
        const isNormal = resolvedStyles.includes('normal');
        const fontWeight = !isNormal && resolvedStyles.includes('bold') ? 'bold' : 'normal';
        const fontStyle = !isNormal && resolvedStyles.includes('italic') ? 'italic' : 'normal';
        const textDecoration = !isNormal && resolvedStyles.includes('underline') ? 'underline' : 'none';
        const fontFamily = getFontStack(settings.templateFontFamily);
        const safeText = escapeHtml(btn.text || 'Call to action');
        const styledText = applyStylesToText(safeText, resolvedStyles);
        const safeUrl = escapeAttribute(btn.url || '#');
        const fallbackId = `legacy-cta-${placement || 'inline'}-${index}`;
        const rawId = typeof btn.id === 'string' && btn.id.trim() ? btn.id.trim() : fallbackId;
        const safeCtaId = escapeAttribute(rawId);
        const linkStyles = [
          'display: inline-block',
          `background-color: ${btn.backgroundColor || settings.linkColor || '#0f172a'}`,
          `color: ${btn.textColor || '#ffffff'}`,
          `font-family: ${fontFamily}`,
          `font-size: ${fontSize}pt`,
          `font-weight: ${fontWeight}`,
          `font-style: ${fontStyle}`,
          `padding: ${padY}px ${padX}px`,
          `border-radius: ${Number(btn.borderRadius) || 0}px`,
          `text-decoration: ${textDecoration}`,
          'margin: 5px',
        ].join('; ');
        const approxTextWidth = Math.max(
          40,
          Math.round((String(btn.text || '').length || 6) * fontSize * 0.6)
        );
        const btnHeight = Math.max(24, fontSize + padY * 2);
        const btnWidth = Math.min(600, approxTextWidth + padX * 2);
        const arcSize = (() => {
          const br = Number(btn.borderRadius) || 0;
          const pct = Math.round((br / btnHeight) * 100);
          return Math.max(0, Math.min(50, pct || 0));
        })();
        return `
            <table role="presentation" border="0" cellpadding="0" cellspacing="0" data-cta-block="true" data-cta-id="${safeCtaId}" style="width: 100%; margin: 10px 0;">
                <tr>
                    <td align="center">
                        <!--[if mso]>
                        <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="${safeUrl}" style="height:${btnHeight}px;v-text-anchor:middle;width:${btnWidth}px;" arcsize="${arcSize}%" stroke="f" fillcolor="${btn.backgroundColor || settings.linkColor || '#0f172a'}">
                          <w:anchorlock/>
                          <center style="color:${btn.textColor || '#ffffff'};font-family:${fontFamily};font-size:${fontSize}pt;font-weight:${fontWeight};font-style:${fontStyle};text-decoration:${textDecoration};">${styledText}</center>
                        </v:roundrect>
                        <![endif]-->
                        <!--[if !mso]><!-- -->
                        <a href="${safeUrl}" data-cta-id="${safeCtaId}" target="_blank" style="${linkStyles}">${styledText}</a>
                        <!--<![endif]-->
                    </td>
                </tr>
            </table>
            `;
      })
      .join('');
  };

  const generateCtaButtonsHtml = placement => {
    if (placement === 'after-intro') {
      const rawContent =
        typeof settings.textTemplateRawContent === 'string'
          ? settings.textTemplateRawContent
          : '';
      const sectionRegex = /\[\[SECTION:\s*([^\]]+)\s*\]\]/i;
      const ctaRegex = /\[\[CTA:\s*([^\]]+)\s*\]\]/gi;
      const rawSectionMatch = sectionRegex.exec(rawContent || '');
      const introRaw = rawSectionMatch
        ? rawContent.slice(0, rawSectionMatch.index)
        : rawContent;
      if (Array.from((introRaw || '').matchAll(ctaRegex)).length > 0) {
        return '';
      }
    }
    const buttons = (settings.ctaButtons || []).filter(
      b => b.placement === placement
    );
    return renderCtaButtonsBlock(buttons, placement);
  };

  const normalizeActionBlockMarker = marker => {
    const normalized = String(marker || '')
      .trim()
      .toUpperCase();
    if (normalized === 'CONGRESS_APPS') return 'CONGRESS_APP_LINKS';
    return normalized;
  };

  const renderActionBlockByMarker = marker => {
    switch (normalizeActionBlockMarker(marker)) {
      case 'WALLET':
        return generateWalletHtml();
      case 'CALENDAR':
        return generateCalendarButtonHtml();
      case 'CONGRESS_APP_LINKS':
        return generateCongressAppHtml();
      default:
        return '';
    }
  };

  const renderTextWithActionMarkers = source => {
    const raw = typeof source === 'string' ? source : '';
    const matches = Array.from(raw.matchAll(ACTION_BLOCK_MARKER_REGEX));
    if (!matches.length) {
      return formatRichText(raw, settings);
    }
    const fragments = [];
    let cursor = 0;
    matches.forEach(match => {
      const start = Number(match.index || 0);
      const end = start + String(match[0] || '').length;
      const textChunk = raw.slice(cursor, start);
      if (textChunk.trim()) {
        fragments.push(formatRichText(textChunk, settings));
      }
      const actionHtml = renderActionBlockByMarker(match[1] || '');
      if (actionHtml) {
        fragments.push(actionHtml);
      }
      cursor = end;
    });
    const tailChunk = raw.slice(cursor);
    if (tailChunk.trim()) {
      fragments.push(formatRichText(tailChunk, settings));
    }
    return fragments.join('');
  };

  const generateOrphanSectionActionGroups = () => {
    const sectionCount = Array.isArray(settings.sectionTitles)
      ? settings.sectionTitles.length
      : 0;
    const sectionPlacementRegex = /^after-section-(\d+)$/;
    const orphanIndexes = new Set();

    const collectIfOrphan = placement => {
      if (typeof placement !== 'string') return;
      const match = placement.match(sectionPlacementRegex);
      if (!match) return;
      const index = Number(match[1]);
      if (!Number.isFinite(index)) return;
      if (index >= sectionCount) orphanIndexes.add(index);
    };

    (settings.ctaButtons || []).forEach(button =>
      collectIfOrphan(button?.placement)
    );

    if (!orphanIndexes.size) return '';
    return Array.from(orphanIndexes)
      .sort((a, b) => a - b)
      .map(index => generateCtaButtonsHtml(`after-section-${index}`))
      .filter(Boolean)
      .join('');
  };

  const generateCalendarButtonHtml = () => {
    if (!settings.includeCalendarButton) return '';

    const defaultProviders = {
      apple: true,
      google: true,
      outlook: false,
      yahoo: false,
    };

    const providers = {
      ...defaultProviders,
      ...(settings.calendarProviders || {}),
    };

    const providerConfigs = [
      {
        key: 'apple',
        label: 'Apple Calendar',
        iconColor: '#111827',
        type: 'ics',
      },
      {
        key: 'google',
        label: 'Google Calendar',
        iconColor: '#0B57CF',
        type: 'google',
      },
      {
        key: 'outlook',
        label: 'Outlook.com',
        iconColor: '#2563EB',
        type: 'outlook',
      },
      {
        key: 'yahoo',
        label: 'Yahoo Calendar',
        iconColor: '#6001D2',
        type: 'yahoo',
      },
    ];

    const activeProviders = providerConfigs.filter(cfg => providers[cfg.key]);
    if (activeProviders.length === 0) return '';

    const headlineText = settings.calendarButtonText || 'Add to calendar';
    const backgroundColor = settings.calendarButtonBackgroundColor || '#f8f9fa';
    const textColor = settings.calendarButtonTextColor || '#1f2937';
    const fontSize = Number(settings.calendarButtonFontSize) || 14;
    const parseSize = (value, fallback) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : fallback;
    };
    const padX = parseSize(settings.calendarButtonPaddingX, 15);
    const padY = parseSize(settings.calendarButtonPaddingY, 15);
    const borderRadius = baseContainerRadius;
    const buttonSize = Math.max(24, Math.min(72, parseSize(settings.calendarProviderLogoHeight, 32)));
    const buttonShape = settings.calendarProviderButtonShape === 'square' ? 'square' : 'circle';
    const iconBorderRadius =
      buttonShape === 'circle' ? '9999px' : `${Math.max(Math.round(buttonSize * 0.25), 6)}px`;
    const fontStack = getFontStack(settings.templateFontFamily);

    const summary = settings.calendarEventTitle || settings.eventName || 'Calendar event';
    const description = settings.calendarEventDescription || '';
    const location = settings.calendarEventLocation || '';
    const eventUrl = settings.calendarEventUrl || '';
    const globalCalendarLogos = {
      apple: globalSettings?.imageUrls?.calendarApple || '',
      google: globalSettings?.imageUrls?.calendarGoogle || '',
      outlook: globalSettings?.imageUrls?.calendarOutlook || '',
      yahoo: globalSettings?.imageUrls?.calendarYahoo || '',
    };
    const providerLogos = {
      ...globalCalendarLogos,
      ...(settings.calendarProviderLogos || {}),
    };
    const providerLabels = settings.calendarProviderLabels || {};

    const startIso = formatDateForCalendar(settings.calendarEventStart);
    let endIso = formatDateForCalendar(settings.calendarEventEnd);
    if (!endIso && startIso && settings.calendarEventStart) {
      const tentativeEnd = new Date(settings.calendarEventStart);
      if (!Number.isNaN(tentativeEnd.getTime())) {
        tentativeEnd.setHours(tentativeEnd.getHours() + 1);
        endIso = formatDateForCalendar(tentativeEnd);
      }
    }

    const dtstamp = formatDateForCalendar(new Date());
    const sanitizedSummary = sanitizeForICS(summary);
    const sanitizedDescription = sanitizeForICS(description);
    const sanitizedLocation = sanitizeForICS(location);
    const sanitizedUrl = sanitizeForICS(eventUrl);

    const icsNeeded = providers.apple || providers.outlook;
    let icsHref = '';
    const safeFileName = sanitizeFileName(settings.calendarButtonFileName);
    if (icsNeeded) {
      const calendarApiOrigin =
        API_ORIGIN ||
        (typeof window !== 'undefined' && window.location?.origin
          ? window.location.origin
          : '');
      const endpointBase = calendarApiOrigin
        ? `${calendarApiOrigin.replace(/\/$/, '')}/api/calendar/ics`
        : '';
      if (endpointBase) {
        const params = new URLSearchParams();
        params.set('summary', summary || 'Calendar event');
        if (description) params.set('description', description);
        if (location) params.set('location', location);
        if (eventUrl) params.set('url', eventUrl);
        if (startIso) params.set('start', startIso);
        if (endIso) params.set('end', endIso);
        params.set('fileName', safeFileName);
        icsHref = `${endpointBase}?${params.toString()}`;
      } else {
        const fallbackLines = [
          'BEGIN:VCALENDAR',
          'VERSION:2.0',
          'PRODID:-//KIT React Email Configurator//EN',
          'CALSCALE:GREGORIAN',
          'BEGIN:VEVENT',
          `UID:${sanitizeForICS(`${summary}-${startIso || Date.now()}@kit-email-configurator`)}`,
          dtstamp ? `DTSTAMP:${dtstamp}` : null,
          startIso ? `DTSTART:${startIso}` : null,
          endIso ? `DTEND:${endIso}` : null,
          `SUMMARY:${sanitizedSummary}`,
          sanitizedDescription ? `DESCRIPTION:${sanitizedDescription}` : null,
          sanitizedLocation ? `LOCATION:${sanitizedLocation}` : null,
          sanitizedUrl ? `URL:${sanitizedUrl}` : null,
          'END:VEVENT',
          'END:VCALENDAR',
          '',
        ].filter(Boolean);
        const icsContent = fallbackLines.join('\r\n');
        icsHref = `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;
      }
    }

    const formatDateForOutlook = value => {
      if (!value) return null;
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return null;
      const pad = n => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    };

    const startOutlook = formatDateForOutlook(settings.calendarEventStart);
    const endOutlook = formatDateForOutlook(settings.calendarEventEnd);

    const formatYahooDuration = () => {
      if (!settings.calendarEventStart || !settings.calendarEventEnd) return '0100';
      const startDate = new Date(settings.calendarEventStart);
      const endDate = new Date(settings.calendarEventEnd);
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        return '0100';
      }
      const diffMs = Math.max(0, endDate.getTime() - startDate.getTime());
      if (diffMs === 0) return '0100';
      const totalMinutes = Math.round(diffMs / 60000);
      const safeMinutes = Math.max(totalMinutes, 60);
      const hours = Math.min(99, Math.floor(safeMinutes / 60));
      const minutes = Math.min(59, safeMinutes % 60);
      return `${String(hours).padStart(2, '0')}${String(minutes).padStart(2, '0')}`;
    };

    const buildProviderLink = provider => {
      switch (provider.type) {
        case 'google': {
          const params = new URLSearchParams({
            action: 'TEMPLATE',
            text: summary,
          });
          if (startIso) {
            const googleDates = endIso ? `${startIso}/${endIso}` : `${startIso}/${startIso}`;
            params.set('dates', googleDates);
          }
          if (description) params.set('details', description);
          if (location) params.set('location', location);
          if (eventUrl) params.set('sprop', eventUrl);
          return {
            href: `https://www.google.com/calendar/render?${params.toString()}`,
            attributes: 'target="_blank" rel="noopener noreferrer"',
          };
        }
        case 'outlook': {
          const params = new URLSearchParams({
            rru: 'addevent',
          });
          if (startOutlook) params.set('startdt', startOutlook);
          if (endOutlook) params.set('enddt', endOutlook || startOutlook);
          if (summary) params.set('subject', summary);
          const bodyParts = [];
          if (description) bodyParts.push(description);
          if (eventUrl) bodyParts.push(eventUrl);
          if (bodyParts.length) params.set('body', bodyParts.join('\n\n'));
          if (location) params.set('location', location);
          return {
            href: `https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&${params.toString()}`,
            attributes: 'target="_blank" rel="noopener noreferrer"',
          };
        }
        case 'yahoo': {
          const params = new URLSearchParams({
            v: '60',
            view: 'd',
            type: '20',
          });
          if (startIso) params.set('st', startIso);
          const yahooDuration = formatYahooDuration();
          if (yahooDuration) params.set('dur', yahooDuration);
          if (summary) params.set('title', summary);
          const descParts = [];
          if (description) descParts.push(description);
          if (eventUrl) descParts.push(eventUrl);
          if (descParts.length) params.set('desc', descParts.join('\n\n'));
          if (location) params.set('in_loc', location);
          return {
            href: `https://calendar.yahoo.com/?${params.toString()}`,
            attributes: 'target="_blank" rel="noopener noreferrer"',
          };
        }
        case 'ics':
        default: {
          return {
            href: icsHref || '#',
            attributes: icsHref
              ? `target="_blank" rel="noopener noreferrer" download="${safeFileName}"`
              : '',
          };
        }
      }
    };

    const summaryAttr = escapeAttribute(summary);
    const labelFontSize = Math.max(fontSize - 4, 10);
    const cellGap = 6;

    const providerCellsHtml = activeProviders
      .map(provider => {
        const { href, attributes } = buildProviderLink(provider);
        if (!href || href === '#') return '';
        const safeHref = escapeAttribute(href);
        const overrideLabel = providerLabels[provider.key];
        const displayLabelRaw = overrideLabel || provider.label;
        const displayLabelHtml = escapeHtml(displayLabelRaw);
        const displayLabelAttr = escapeAttribute(displayLabelRaw);
        const logoOverride = providerLogos[provider.key];
        const logoOverrideAttr = logoOverride ? escapeAttribute(logoOverride) : '';
        const fallbackLogo = getCalendarProviderLogo(provider.key);
        const fallbackLogoAttr = fallbackLogo ? escapeAttribute(fallbackLogo) : '';
        const initials = (displayLabelRaw || provider.label || provider.key)
          .split(/\s+/)
          .map(word => word[0])
          .join('')
          .slice(0, 2)
          .toUpperCase();
        const initialsHtml = escapeHtml(initials || provider.key.slice(0, 2).toUpperCase());
        const attrParts = [];
        if (attributes) attrParts.push(attributes);
        attrParts.push(`title="Add to ${displayLabelAttr}"`);
        attrParts.push(`aria-label="Add ${summaryAttr} to ${displayLabelAttr}"`);
        const attributeSegment = attrParts.length ? ` ${attrParts.join(' ')}` : '';

        const wrapperBackground = logoOverrideAttr ? '#ffffff' : provider.iconColor;

        const iconWrapperStyle = [
          'display:inline-flex',
          'align-items:center',
          'justify-content:center',
          `width:${buttonSize}px`,
          `height:${buttonSize}px`,
          `border-radius:${iconBorderRadius}`,
          `background:${wrapperBackground}`,
          'overflow:hidden',
          logoOverrideAttr ? 'border:1px solid rgba(15,23,42,0.12)' : '',
        ]
          .filter(Boolean)
          .join(';');

        let iconMarkup = '';
        if (logoOverrideAttr) {
          iconMarkup = `<img src="${logoOverrideAttr}" alt="${displayLabelAttr}" style="display:block;width:100%;height:100%;object-fit:contain;border-radius:${iconBorderRadius};">`;
        } else if (fallbackLogoAttr) {
          iconMarkup = `<img src="${fallbackLogoAttr}" alt="${displayLabelAttr}" style="display:block;width:85%;height:85%;object-fit:contain;">`;
        } else {
          iconMarkup = `<span style="display:inline-flex;align-items:center;justify-content:center;width:100%;height:100%;border-radius:${iconBorderRadius};background:${provider.iconColor};color:#ffffff;font-size:${Math.max(
            Math.round(buttonSize * 0.4),
            10
          )}pt;font-weight:600;">${initialsHtml}</span>`;
        }

        const anchorStyle = `display:inline-flex;flex-direction:column;align-items:center;gap:6px;text-decoration:none;color:${textColor};font-family:${fontStack};font-size:${labelFontSize}pt;min-width:${buttonSize}px;`;

        return `<td style="padding:0;" align="center">
                  <a href="${safeHref}"${attributeSegment} data-kit-calendar-button="true" data-kit-calendar-provider="${provider.key}" style="${anchorStyle}">
                    <span style="${iconWrapperStyle}">
                      ${iconMarkup}
                    </span>
                    <span style="display:block;white-space:nowrap;">${displayLabelHtml}</span>
                  </a>
                </td>`;
      })
      .filter(Boolean)
      .join('');

    if (!providerCellsHtml) return '';

    const headlineHtml = headlineText
      ? `<p style="margin:0 0 14px 0;font-size:${fontSize}pt;line-height:1.4;color:${textColor};font-family:${fontStack};text-align:center;">${escapeHtml(headlineText)}</p>`
      : '';
    const providersBlockHtml = `
            <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="margin:0 auto;border-collapse:separate;border-spacing:${cellGap}px; width:100%; max-width:100%; text-align:center;">
              <tr>
                ${providerCellsHtml}
              </tr>
            </table>
          `;

    return `
            <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="width: 100%; margin: 20px 0;">
                <tr>
                    <td data-kit-calendar-wrapper="true" style="background-color:${backgroundColor}; padding:${padY}px ${padX}px; border-radius:${borderRadius}px;">
                      ${headlineHtml}
                      ${providersBlockHtml}
                    </td>
                </tr>
            </table>
        `;
  };

  const getSectionHeadingTag = section => {
    const level = Number(section?.level);
    if (Number.isFinite(level) && level >= 1 && level <= 6) {
      return `h${level}`;
    }
    return 'h3';
  };

  // Generate section titles HTML
  const generateSectionTitlesHtml = () => {
    return (settings.sectionTitles || [])
      .map((sec, i) => {
        const headingTag = getSectionHeadingTag(sec);
        const headingColor = sec?.color || settings.linkColor || '#00519e';
        const headingFontSize = Number(sec?.fontSize) || 18;
        const lineWidth = settings.lineWidth || 1;
        const titleText =
          typeof sec?.text === 'string' ? sec.text.trim() : '';
        const bodyText =
          typeof sec?.body === 'string' && sec.body.trim()
            ? sec.body
            : typeof sec?.content === 'string' && sec.content.trim()
              ? sec.content
              : typeof sec?.bodyText === 'string' && sec.bodyText.trim()
                ? sec.bodyText
                : '';
        if (!titleText && !bodyText) return '';
        const { guardOpen, guardClose, body: guardedBodyText } =
          extractSectionGuard(bodyText);
        const safeTitle = titleText || `Section ${i + 1}`;
        const safeTitleAttr = escapeAttribute(safeTitle);
        const bodyHtml = guardedBodyText
          ? renderTextWithActionMarkers(guardedBodyText)
          : '';
        const styledTitle = applyStylesToText(safeTitle, sec?.styles);
        const bodyBlock = bodyHtml
          ? `
            <table role="presentation" border="0" cellpadding="0" cellspacing="0" data-kit-section-index="${i}" data-kit-section-role="body" style="width: 100%; margin-bottom: 16px;">
                <tr>
                    <td data-kit-section-index="${i}" data-kit-section-role="body-text">
                        ${bodyHtml}
                    </td>
                </tr>
            </table>
            `
          : '';
        const sectionHtml = `
            ${createOutlookSpacer(20)}
            <table role="presentation" border="0" cellpadding="0" cellspacing="0" data-kit-section-index="${i}" data-kit-section-role="heading" style="width: 100%; margin: 24px 0 16px 0;">
                <tr>
                    <td>
                        <${headingTag} data-kit-section-index="${i}" data-kit-section-role="heading-text" data-kit-section-text="${safeTitleAttr}" style="color: ${headingColor}; font-size: ${headingFontSize}pt; border-bottom: ${lineWidth}px solid ${headingColor}; padding-bottom: 8px; margin: 0; font-family: ${getFontStack(settings.templateFontFamily)};">
                            ${styledTitle}
                        </${headingTag}>
                    </td>
                </tr>
            </table>
            ${bodyBlock}
            ${generateCtaButtonsHtml(`after-section-${i}`)}
        `;
        if (guardOpen && guardClose) {
          return `${guardOpen}
            ${sectionHtml}
            ${guardClose}`;
        }
        return sectionHtml;
      })
      .join('');
  };

  // Generate signature HTML
  const generateSignatureHtml = () => {
    const lineWidth = settings.lineWidth || 1;
    const dividerColor = settings.dividerColor;

    const closingSource = resolveLocalizedText(
      settings.closingText,
      settings.closingTextTranslations,
      settings.textTemplateLanguage
    );
    const closingLine = escapeHtml(closingSource || 'Best regards,');
    let signature = `
            <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="width: 100%; margin: 24px 0 0 0;">
                <tr>
                    <td style="padding-bottom: 8px;">
                        <p style="font-size: ${settings.teamNameFontSize}pt; line-height: 1.6; margin: 0; color: ${settings.bodyTextColor}; font-family: ${getFontStack(settings.templateFontFamily)};">
                            ${closingLine}<br><br>
                            ${applyStylesToText(settings.organizingTeamName, settings.teamNameStyles)}
            `;
    if (settings.includeCoName)
      signature += `<br><span style="font-size: ${settings.bodyFontSize}pt; color: #666;">c./o. K.I.T. Group GmbH</span>`;
    if (settings.includeAssociation)
      signature += `<br><span style="font-size: ${settings.bodyFontSize}pt; color: #666;">Association & Conference Management</span>`;
    if (settings.includeAddress)
      signature += `<br><span style="font-size: ${settings.bodyFontSize}pt;">Kurfürstendamm 71<br>10709 Berlin, Germany</span>`;

    signature += `
                        </p>
                    </td>
                </tr>
                <tr>
                    <td style="padding-top: 16px;">
                        <table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0">
                            <tr>
                                <td style="border-top: ${lineWidth}px solid ${dividerColor}; font-size: 0; line-height: 0; height: 0; mso-line-height-rule: exactly;">&nbsp;</td>
                            </tr>
                        </table>
                    </td>
                </tr>
                <tr>
                    <td style="line-height: ${DIVIDER_GAP_PX}px; height: ${DIVIDER_GAP_PX}px; font-size: 0; mso-line-height-rule: exactly;">&nbsp;</td>
                </tr>
            </table>
        `;

    return signature;
  };

  // Generate contact info HTML
  const generateContactInfoHtml = () => {
    const rows = [];
    const baseOrder = ['email', 'phone', 'website'];
    const rawOrder = Array.isArray(settings.contactOrder)
      ? settings.contactOrder.filter(key => baseOrder.includes(key))
      : [];
    const orderedMethods = [
      ...rawOrder,
      ...baseOrder.filter(key => !rawOrder.includes(key)),
    ];

    orderedMethods.forEach(method => {
      if (method === 'email' && settings.showEmail && settings.contactEmail) {
        rows.push({
          icon:
            (globalSettings &&
              globalSettings.imageUrls &&
              globalSettings.imageUrls.iconEmail) ||
            '',
          href: `mailto:${settings.contactEmail}`,
          text: settings.contactEmail,
        });
      }
      if (method === 'phone' && settings.showPhone && settings.contactPhone) {
        rows.push({
          icon:
            (globalSettings &&
              globalSettings.imageUrls &&
              globalSettings.imageUrls.iconPhone) ||
            '',
          href: `tel:${settings.contactPhone}`,
          text: settings.contactPhone,
        });
      }
      if (method === 'website' && settings.showWebsite) {
        const websiteHref = normalizeWebsiteHref(settings.websiteUrl);
        if (websiteHref) {
          rows.push({
            icon:
              (globalSettings &&
                globalSettings.imageUrls &&
                globalSettings.imageUrls.iconWebsite) ||
              '',
            href: websiteHref,
            text: deriveWebsiteLinkText(
              settings.websiteDisplayText,
              settings.websiteUrl
            ),
            target: '_blank',
          });
        }
      }
    });

    if (rows.length === 0) return '';

    const rowHtml = rows
      .map(
        r => `
            <tr>
                <td style="width: 18px; padding: 2px 8px 2px 0; vertical-align: middle;">
                    <img src="${r.icon}" alt="" role="presentation" width="16" height="16" style="display: block; border: 0;">
                </td>
                <td style="vertical-align: middle; mso-line-height-rule: exactly; font-size: ${settings.bodyFontSize}pt; line-height: 1.4; color: ${settings.bodyTextColor}; font-family: ${getFontStack(settings.templateFontFamily)};">
                    <a href="${r.href}" ${r.target ? `target="${r.target}"` : ''} style="color: ${settings.linkColor}; text-decoration: none;">${r.text}</a>
                </td>
            </tr>
        `
      )
      .join('');

    return `
            <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="width: 100%; margin-top: 0;">
                <tr>
                    <td>
                        <table role="presentation" border="0" cellpadding="0" cellspacing="0">
                            ${rowHtml}
                        </table>
                    </td>
                </tr>
            </table>
        `;
  };

  // Generate social media HTML
  const generateCongressAppHtml = () => {
    if (!settings.showCongressAppLinks) return '';

    const DEFAULT_BADGE_HEIGHT = 48;
    const STORE_BADGE_HEIGHTS = {
      appleUrl: 48,
      googleUrl: 72,
    };

    const stores = [
      {
        key: 'appleUrl',
        imageKey: 'congressAppApple',
        alt: 'Download on the App Store',
      },
      {
        key: 'googleUrl',
        imageKey: 'congressAppGoogle',
        alt: 'Get it on Google Play',
      },
    ];

    const availableStores = stores
      .map(store => {
        const href = settings.congressAppLinks?.[store.key];
        if (!href) return null;
        const imgSrc =
          (globalSettings &&
            globalSettings.imageUrls &&
            globalSettings.imageUrls[store.imageKey]) ||
          '';
        return {
          ...store,
          href,
          imgSrc,
          targetHeight: STORE_BADGE_HEIGHTS[store.key] || DEFAULT_BADGE_HEIGHT,
        };
      })
      .filter(Boolean);

    if (!availableStores.length) return '';

    const backgroundColor =
      settings.congressAppBackgroundColor || '#f8f9fa';
    const headline = settings.congressAppHeadline || 'Get the congress app';
    const bodyColor = settings.bodyTextColor || '#1f2937';

    const maxBadgeHeight = availableStores.reduce(
      (max, store) =>
        Math.max(max, store.targetHeight || DEFAULT_BADGE_HEIGHT),
      DEFAULT_BADGE_HEIGHT
    );

    const badgeHtml = availableStores
      .map(store => {
        const targetHeight = store.targetHeight || DEFAULT_BADGE_HEIGHT;
        const badgeContent = store.imgSrc
          ? `<img src="${store.imgSrc}" alt="${store.alt}" height="${targetHeight}" style="display: block; border: 0; height: ${targetHeight}px; max-height: ${targetHeight}px; width: auto; max-width: 100%;">`
          : `<span style="display: inline-flex; align-items: center; justify-content: center; padding: 10px 16px; border-radius: 999px; background-color: ${settings.linkColor}; color: #ffffff; font-family: ${getFontStack(
              settings.templateFontFamily
            )}; font-size: 13pt; line-height: 1.2; height: ${maxBadgeHeight}px;">${store.alt}</span>`;
        return `
                    <a href="${store.href}" target="_blank" style="text-decoration: none; display: inline-flex; align-items: center; justify-content: center; margin: 0 8px; min-height: ${maxBadgeHeight}px; vertical-align: middle; line-height: 0;">
                        ${badgeContent}
                    </a>
                `;
      })
      .join('');

    return `
            <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="width: 100%; margin-top: 20px;">
                <tr>
                    <td style="text-align: center; background-color: ${backgroundColor}; padding: 15px; border-radius: ${baseContainerRadius}px;">
                        <p style="font-size: 14pt; color: ${bodyColor}; margin: 0 0 10px 0; font-family: ${getFontStack(
                          settings.templateFontFamily
                        )};">
                            ${headline}
                        </p>
                        <div style="font-size: 0; line-height: 0;">
                            ${badgeHtml}
                        </div>
                    </td>
                </tr>
            </table>
        `;
  };

  const generateSocialMediaHtml = () => {
    if (!settings.showSocialMedia) return '';

    const socialLinks = [
      { key: 'facebookUrl', icon: 'socialFacebook', label: 'Facebook' },
      { key: 'twitterUrl', icon: 'socialX', label: 'X' },
      { key: 'linkedinUrl', icon: 'socialLinkedIn', label: 'LinkedIn' },
      { key: 'instagramUrl', icon: 'socialInstagram', label: 'Instagram' },
      { key: 'youtubeUrl', icon: 'socialYoutube', label: 'YouTube' },
      { key: 'miscUrl', icon: 'socialMisc', label: 'Other' },
    ];

    const hasSocialLinks = socialLinks.some(
      link => settings.socialLinks && settings.socialLinks[link.key]
    );
    if (!hasSocialLinks) return '';
    const socialHeaderFontSize = Number(settings.bodyFontSize) || 14;
    const socialFooterText = (settings.socialFooterText || '').trim();
    const socialHeaderText = applyStylesToText(
      settings.socialTextInput || 'Follow us',
      settings.socialHeaderStyles
    );
    const socialFooterHtml = socialFooterText
      ? `<p style="font-size: ${socialHeaderFontSize}pt; color: ${settings.bodyTextColor}; margin: 10px 0 0 0; font-family: ${getFontStack(settings.templateFontFamily)};">${applyStylesToText(socialFooterText, settings.socialFooterStyles)}</p>`
      : '';

    let socialHtml = `
            <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="width: 100%; margin-top: 20px;">
                <tr>
                    <td style="text-align: center; background-color: ${settings.socialMediaBackgroundColor}; padding: 15px; border-radius: ${baseContainerRadius}px;">
                        <p style="font-size: ${socialHeaderFontSize}pt; color: ${settings.bodyTextColor}; margin: 0 0 10px 0; font-family: ${getFontStack(settings.templateFontFamily)};">
                            ${socialHeaderText}
                        </p>
                        <div style="font-size: 0; line-height: 0;">
        `;

    socialLinks.forEach(link => {
      if (settings.socialLinks && settings.socialLinks[link.key]) {
        const iconSize = settings.socialIconSize || 16;
        const borderRadius = settings.socialIconBorderRadius || 0;
        socialHtml += `
                    <a href="${settings.socialLinks[link.key]}" aria-label="${link.label}" style="text-decoration: none; margin: 0 5px; display: inline-block;">
                        <img src="${(globalSettings && globalSettings.imageUrls && globalSettings.imageUrls[link.icon]) || ''}" alt="" role="presentation" width="${iconSize}" height="${iconSize}" style="border: 0; border-radius: ${borderRadius}px; display: inline-block; vertical-align: middle;">
                    </a>
                `;
      }
    });

    socialHtml += `
                        </div>
                        ${socialFooterHtml}
                    </td>
                </tr>
            </table>
        `;

    return socialHtml;
  };

  const buildTextTemplateIntroHtml = () => {
    const content =
      typeof settings.textTemplateContent === 'string'
        ? settings.textTemplateContent.trim()
        : '';
    const rawContent =
      typeof settings.textTemplateRawContent === 'string'
        ? settings.textTemplateRawContent
        : '';
    const sectionRegex = /\[\[SECTION:\s*([^\]]+)\s*\]\]/i;
    const ctaRegex = /\[\[CTA:\s*([^\]]+)\s*\]\]/gi;
    const rawSectionMatch = sectionRegex.exec(rawContent || '');
    const introRaw = rawSectionMatch
      ? rawContent.slice(0, rawSectionMatch.index)
      : rawContent;
    const inlineCtaMatches = Array.from((introRaw || '').matchAll(ctaRegex));
    const introButtons = (settings.ctaButtons || []).filter(
      button => button?.placement === 'after-intro'
    );
    if (!content && (settings.sectionTitles || []).length) {
      return '';
    }
    if (inlineCtaMatches.length > 0) {
      const fragments = [];
      let cursor = 0;
      inlineCtaMatches.forEach((match, index) => {
        const start = Number(match.index || 0);
        const end = start + String(match[0] || '').length;
        const textChunk = introRaw.slice(cursor, start).trim();
        if (textChunk) {
          fragments.push(renderTextWithActionMarkers(textChunk));
        }
        const buttonFromPlacement = introButtons[index] || null;
        if (buttonFromPlacement) {
          fragments.push(renderCtaButtonsBlock([buttonFromPlacement], 'after-intro-inline'));
        } else {
          const parsed = parseInlineCtaMarker(match[1] || '');
          const fallbackButton = {
            id: `inline-cta-${index}`,
            text: parsed.text || 'Call to action',
            url: parsed.url || '#',
            backgroundColor: settings.linkColor || '#0f172a',
            textColor: '#ffffff',
            fontSize: 16,
            paddingX: 20,
            paddingY: 10,
            borderRadius: 6,
            styles: ['bold'],
            placement: 'after-intro',
          };
          fragments.push(renderCtaButtonsBlock([fallbackButton], 'after-intro-inline'));
        }
        cursor = end;
      });
      const tailChunk = introRaw.slice(cursor).trim();
      if (tailChunk) {
        fragments.push(renderTextWithActionMarkers(tailChunk));
      }
      return fragments.filter(Boolean).join('');
    }
    const bodyText = content
      ? content
      : 'This is a sample paragraph. Please replace this with your email content.';
    const bodyHtml = renderTextWithActionMarkers(bodyText);
    return `
                    <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="width: 100%;">
                        <tr>
                            <td>
                                ${bodyHtml}
                            </td>
                        </tr>
                    </table>
    `;
  };

  // Generate main content HTML
  const generateMainContentHtml = () => {
    // Regmail-specific layout
    if (isRegmail) {
      return generateRegmailContentHtml();
    }

    // Default content for other templates
    return `
            <tr>
                <td style="padding: 24px;">
                    <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="width: 100%;">
                        <tr>
                            <td>
                                <p style="font-size: ${settings.salutationFontSize}pt; line-height: ${settings.paragraphLineHeight}; margin: 0 0 16px 0; color: ${settings.bodyTextColor}; font-family: ${getFontStack(settings.templateFontFamily)};">
                                    ${applyStylesToText(
                                      resolveLocalizedText(
                                        settings.salutationInput,
                                        settings.salutationTranslations,
                                        settings.textTemplateLanguage
                                      ),
                                      settings.salutationStyles
                                    )}
                                </p>
                            </td>
                        </tr>
                    </table>
                    
                    ${generateCtaButtonsHtml('after-salutation')}

                    ${buildTextTemplateIntroHtml()}
                    
                    ${generateCtaButtonsHtml('after-intro')}

                    ${generateSectionTitlesHtml()}
                    ${generateOrphanSectionActionGroups()}

                    ${generateCtaButtonsHtml('before-signature')}

                    ${generateSignatureHtml()}
                    ${generateContactInfoHtml()}
                    ${generateConfidentialityNoteHtml()}
                    ${generateSocialMediaHtml()}
                </td>
            </tr>
        `;
  };

  // Generate Regmail-specific content HTML
  const generateRegmailContentHtml = () => {
    // Helper function to format event time from start and end dates
    const formatEventTime = () => {
      if (settings.eventStartDate && settings.eventEndDate) {
        const startDate = new Date(settings.eventStartDate);
        const endDate = new Date(settings.eventEndDate);

        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          const startFormatted = startDate.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
          });
          const endFormatted = endDate.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          });

          // If same month, format as "October 15-18, 2024"
          if (
            startDate.getMonth() === endDate.getMonth() &&
            startDate.getFullYear() === endDate.getFullYear()
          ) {
            return `${startFormatted}-${endDate.getDate()}, ${endDate.getFullYear()}`;
          }
          // If different months but same year, format as "October 15 - November 18, 2024"
          else if (startDate.getFullYear() === endDate.getFullYear()) {
            return `${startFormatted} - ${endFormatted}`;
          }
          // If different years, format as "October 15, 2024 - November 18, 2025"
          else {
            return `${startFormatted}, ${startDate.getFullYear()} - ${endFormatted}`;
          }
        }
      }
      // Fall back to server-side variable placeholder if not set in editor
      return '$(eventTime)';
    };

    // Helper function to format payment due date
    const formatPaymentDueDate = () => {
      if (settings.paymentDueDate) {
        const date = new Date(settings.paymentDueDate);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          });
        }
      }
      // Fall back to server-side variable placeholder if not set in editor
      return '$(paymentDueDate)';
    };

    // Helper function to get section title with fallback
    const getSectionTitle = (index, fallback) => {
      if (settings.sectionTitles && settings.sectionTitles[index]) {
        const section = settings.sectionTitles[index];
        return {
          text: section.text || fallback,
          color: section.color || settings.linkColor || '#00519e',
          fontSize: section.fontSize || 18,
          styles: section.styles || ['bold'],
          level: section.level,
        };
      }
      return {
        text: fallback,
        color: settings.linkColor || '#00519e',
        fontSize: 18,
        styles: ['bold'],
        level: 3,
      };
    };

    // Helper function to generate section title HTML
    const generateSectionTitleHtml = (index, fallback) => {
      const section = getSectionTitle(index, fallback);
      const headingTag = getSectionHeadingTag(section);
      return `
                ${createOutlookSpacer(20)}
                <table role="presentation" border="0" cellpadding="0" cellspacing="0" data-kit-section-index="${index}" data-kit-section-role="heading" style="width: 100%; margin: 24px 0 8px 0;">
                    <tr>
                        <td>
                            <${headingTag} data-kit-section-index="${index}" data-kit-section-role="heading-text" data-kit-section-text="${escapeAttribute(section.text)}" style="color: ${section.color}; font-size: ${section.fontSize}pt; border-bottom: ${settings.lineWidth || 1}px solid ${section.color}; padding-bottom: 8px; margin: 0; font-family: ${getFontStack(settings.templateFontFamily)};">
                                ${applyStylesToText(section.text, section.styles)}
                            </${headingTag}>
                        </td>
                    </tr>
                </table>
            `;
    };

    return `
            <tr>
                <td style="padding: 24px;">
                    <!-- Greeting -->
                    <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="width: 100%; margin: 0 0 16px 0;">
                        <tr>
                            <td>
                                <p style="font-size: ${settings.salutationFontSize}pt; line-height: ${settings.paragraphLineHeight}; margin: 0 0 12px 0; color: ${settings.bodyTextColor}; font-family: ${getFontStack(settings.templateFontFamily)};">
                                ${applyStylesToText(
                                  resolveLocalizedText(
                                    settings.salutationInput,
                                    settings.salutationTranslations,
                                    settings.textTemplateLanguage
                                  ) || `Dear \${p.firstName} \${p.lastName},`,
                                  settings.salutationStyles
                                )}
                                </p>
                                <p style="font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; margin: 0 0 12px 0; color: ${settings.bodyTextColor}; font-family: ${getFontStack(settings.templateFontFamily)};">
                                    Thank you for submitting your hotel booking for ${settings.eventName || '$(eventName)'}, hosted by ${settings.hostingOrganisation || '$(hostingOrganisation)'} to be held in ${settings.eventLocation || '$(eventLocation)'} from ${formatEventTime()}.
                                </p>
                                <p style="font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; margin: 0; color: ${settings.bodyTextColor}; font-family: ${getFontStack(settings.templateFontFamily)};">
                                    Please note that this message is NOT your confirmation. We have received your booking request and, upon receipt of full payment, will send you confirmation of your booking by email. There is no need to reply to this email if the information below is correct.
                                </p>
                            </td>
                        </tr>
                    </table>

                    <!-- Personal details -->
                    ${generateSectionTitleHtml(0, 'Personal details')}
                    <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="width: 100%; margin: 0 0 16px 0;">
                        <tr class="stack">
                            <td width="50%" style="width: 50%; padding: 10px 10px 0 0; vertical-align: top;">
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: #666666;"><strong>Name</strong></p>
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor};">\${p.name3}</p>
                            </td>
                            <td width="50%" style="width: 50%; padding: 10px 0 0 10px; vertical-align: top;">
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: #666666;"><strong>Email</strong></p>
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor};">\${p.communication.email1}</p>
                            </td>
                        </tr>
                        <tr>
                            <td colspan="2" style="padding: 10px 0 0 0; vertical-align: top;">
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: #666666;"><strong>Country of residence</strong></p>
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor};">\${p.mainCountry.getString('en')}</p>
                            </td>
                        </tr>
                    </table>

                    <!-- Accommodation -->
                    ${generateSectionTitleHtml(1, 'Accommodation')}

                    <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="width: 100%; margin: 0 0 8px 0;">
                        <%
                            int hbIndex = 0;
                            for (hb in p.hotelBookings) {
                                if (!hb.isCanceled()) {
                        %>

                        <% if (hbIndex++ > 0) { %>
                        <tr>
                            <td colspan="2" style="padding: 12px 0;">
                                <div style="border-top: 1px solid ${settings.dividerColor}; line-height: 1px; height: 1px;">&nbsp;</div>
                            </td>
                        </tr>
                        <% } %>

                        <tr>
                            <td colspan="2" style="padding: 10px 0 0 0; vertical-align: top;">
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: #666666;"><strong>Guest Name</strong></p>
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor};">\${hb.getBenefitRecipientNames(false)}</p>
                            </td>
                        </tr>

                        <tr class="stack">
                            <td width="50%" style="width: 50%; padding: 10px 10px 0 0; vertical-align: top;">
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: #666666;"><strong>Hotel</strong></p>
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor};">\${hb.hotelName}</p>
                            </td>
                            <td width="50%" style="width: 50%; padding: 10px 0 0 10px; vertical-align: top;">
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: #666666;"><strong>Room type / Rate per night</strong></p>
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor};">\${hb.roomDefinition.name} / \${hb.amountPerNightBrutto}</p>
                            </td>
                        </tr>

                        <tr class="stack">
                            <td width="50%" style="width: 50%; padding: 10px 10px 0 0; vertical-align: top;">
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: #666666;"><strong>Arrival</strong></p>
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor};">\${mediumDate.format(hb.arrival)}</p>
                            </td>
                            <td width="50%" style="width: 50%; padding: 10px 0 0 10px; vertical-align: top;">
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: #666666;"><strong>Departure</strong></p>
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor};">\${mediumDate.format(hb.departure)}</p>
                            </td>
                        </tr>

                        <% if (hb.isLateArrival() || hb.arrivalNote != null) { %>
                        <tr>
                            <td colspan="2" style="padding: 10px 0 0 0; vertical-align: top;">
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: #666666;"><strong>Late Arrival</strong></p>
                                <% if (hb.arrivalNote != null) { %>
                                    <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor};">\${hb.arrivalNote}</p>
                                <% } else { %>
                                    <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor};">Yes</p>
                                <% } %>
                            </td>
                        </tr>
                        <% } %>

                        <% if (hb.hotelInfo != null) { %>
                        <tr>
                            <td colspan="2" style="padding: 10px 0 0 0; vertical-align: top;">
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: #666666;"><strong>Remarks</strong></p>
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor};">\${hb.hotelInfo}</p>
                            </td>
                        </tr>
                        <% } %>

                        <% }} %>
                    </table>

                    <!-- Payment section -->
                    ${generateSectionTitleHtml(2, 'Payment')}
                    <% if ( sh.isNotEmpty(paymentType) ) { %>
                    <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="width: 100%; margin: 0 0 16px 0;">
                        <% if (paymentType == "BANK_TRANSFER") { %>
                        <tr>
                            <td colspan="2" style="padding: 10px 0 0 0; vertical-align: top;">
                                <p style="margin: 0 0 12px 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor}; font-family: ${getFontStack(settings.templateFontFamily)};">
                                    You have chosen to pay by bank transfer.
                                </p>
                                <p style="margin: 0 0 12px 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor}; font-family: ${getFontStack(settings.templateFontFamily)};">
                                    Please transfer your payment in EUR to:
                                </p>
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor}; font-family: ${getFontStack(settings.templateFontFamily)};">
                                    KIT Group GmbH, Kurfürstendamm 71, 10709 Berlin<br/>
                                    Commerzbank, Kurfürstendamm 237, 10719 Berlin, Germany
                                </p>
                            </td>
                        </tr>
                        <tr class="stack">
                            <td width="50%" style="width: 50%; padding: 6px 10px 0 0; vertical-align: top;">
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: #666666; font-family: ${getFontStack(settings.templateFontFamily)};">K.I.T. account number:</p>
                            </td>
                            <td width="50%" style="width: 50%; padding: 6px 0 0 10px; vertical-align: top;">
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor}; font-family: ${getFontStack(settings.templateFontFamily)};">\${sh.avoidNull(event.bank.bankAccountNumber)}</p>
                            </td>
                        </tr>
                        <tr class="stack">
                            <td width="50%" style="width: 50%; padding: 6px 10px 0 0; vertical-align: top;">
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: #666666; font-family: ${getFontStack(settings.templateFontFamily)};">Bank code:</p>
                            </td>
                            <td width="50%" style="width: 50%; padding: 6px 0 0 10px; vertical-align: top;">
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor}; font-family: ${getFontStack(settings.templateFontFamily)};">\${sh.avoidNull(event.bank.bankIdentifierCode)}</p>
                            </td>
                        </tr>
                        <tr class="stack">
                            <td width="50%" style="width: 50%; padding: 6px 10px 0 0; vertical-align: top;">
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: #666666; font-family: ${getFontStack(settings.templateFontFamily)};">Swift:</p>
                            </td>
                            <td width="50%" style="width: 50%; padding: 6px 0 0 10px; vertical-align: top;">
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor}; font-family: ${getFontStack(settings.templateFontFamily)};">\${sh.avoidNull(event.bank.bic)}</p>
                            </td>
                        </tr>
                        <tr class="stack">
                            <td width="50%" style="width: 50%; padding: 6px 10px 0 0; vertical-align: top;">
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: #666666; font-family: ${getFontStack(settings.templateFontFamily)};">IBAN:</p>
                            </td>
                            <td width="50%" style="width: 50%; padding: 6px 0 0 10px; vertical-align: top;">
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor}; font-family: ${getFontStack(settings.templateFontFamily)};">\${sh.avoidNull(event.bank.iban)}</p>
                            </td>
                        </tr>
                        <tr class="stack">
                            <td width="50%" style="width: 50%; padding: 6px 10px 0 0; vertical-align: top;">
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: #666666; font-family: ${getFontStack(settings.templateFontFamily)};">Reference:</p>
                            </td>
                            <td width="50%" style="width: 50%; padding: 6px 0 0 10px; vertical-align: top;">
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor}; font-family: ${getFontStack(settings.templateFontFamily)};">\${reference}</p>
                            </td>
                        </tr>
                        <tr>
                            <td colspan="2" style="padding: 10px 0 0 0; vertical-align: top;">
                                <p style="margin: 0 0 12px 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor}; font-family: ${getFontStack(settings.templateFontFamily)};">
                                    The sender must prepay all transfer costs (including charges that may be incurred on the receiving bank end). Please list the reference information (as indicated above) on the transfer slip.
                                </p>
                            </td>
                        </tr>
                        <% } %>
                        <% if (paymentType == "ONLINE_PAYMENT") { %>
                        <tr>
                            <td colspan="2" style="padding: 10px 0 0 0; vertical-align: top;">
                                <p style="margin: 0 0 12px 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor}; font-family: ${getFontStack(settings.templateFontFamily)};">
                                    You have chosen to pay by credit card.
                                </p>
                            </td>
                        </tr>
                        <% } %>
                        <tr>
                            <td colspan="2" style="padding: 10px 0 0 0; vertical-align: top;">
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor}; font-family: ${getFontStack(settings.templateFontFamily)}; mso-line-height-rule: exactly;">
                                    You will receive an official confirmation upon receipt of the payment. Payment is due within 7 working days of placing your booking. If there are fewer than 7 working days between the time of placing the booking and ${formatPaymentDueDate()}, then the payment must be made by ${formatPaymentDueDate()}. If payment is not received, the booking will be automatically cancelled.
                                </p>
                            </td>
                        </tr>
                    </table>
                    <% } %>

                    <!-- Changes and cancellation -->
                    ${generateSectionTitleHtml(3, 'Changes and cancellation')}
                    <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="width: 100%; margin: 0 0 16px 0;">
                        <tr>
                            <td style="padding: 10px 0 0 0; vertical-align: top;">
                                <p style="margin: 0 0 12px 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor}; font-family: ${getFontStack(settings.templateFontFamily)};">
                                    If you need to make changes to the data you entered in our booking form, please contact
                                    <a href="mailto:${settings.eventEmail || '$(eventEmail)'}" style="color: ${settings.linkColor}; text-decoration: none;">${settings.eventEmail || '$(eventEmail)'}</a>. Do NOT re-send the booking form.
                                </p>
                                <p style="margin: 0; font-size: ${settings.bodyFontSize}pt; line-height: ${settings.paragraphLineHeight}; color: ${settings.bodyTextColor}; font-family: ${getFontStack(settings.templateFontFamily)}; mso-line-height-rule: exactly;">
                                    All changes and cancellations must be sent in writing (by email) to ${settings.organizingTeamName || '$(teamName)'} c/o K.I.T. Group. Please note that hotels will not accept any changes or cancellations directly.
                                    For cancellation conditions, please refer to the general terms and conditions as agreed to in the online booking process. For a reduction in the length of stay, no refunds will be made after ${settings.paymentDueDate ? formatPaymentDueDate() : '$(paymentdueDate)'}. In the case of a no-show, the room will be released at 09:00 after the first day of the booking, and the accommodation will not be refunded.
                                </p>
                            </td>
                        </tr>
                    </table>

                    ${generateSignatureHtml()}
                    ${generateContactInfoHtml()}
                    ${generateConfidentialityNoteHtml()}
                    ${generateSocialMediaHtml()}
                </td>
            </tr>
        `;
  };

  // Get banner sections
  const topBannersBefore = getBanners('top', 'before');
  const topBannersAfter = getBanners('top', 'after');
  const bottomBannersBefore = getBanners('bottom', 'before');
  const headerAreaHtml = generateHeaderAreaHtml();
  const footerHtml = generateFooterHtml();
  const bottomBannersAfter = getBanners('bottom', 'after');

  const templateTitle = (() => {
    if (settings?.name && String(settings.name).trim()) {
      return escapeHtml(settings.name);
    }
    if (settings?.eventName && String(settings.eventName).trim()) {
      return escapeHtml(settings.eventName);
    }
    return 'Email Template';
  })();

  // Build the complete HTML
  const html = `<!DOCTYPE html>
<html lang="en" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta http-equiv="X-UA-Compatible" content="IE=edge">
    <title>${templateTitle}</title>
    ${getGoogleFontsImport(settings.templateFontFamily)}
    <!--[if mso]>
    <noscript>
        <xml>
            <o:OfficeDocumentSettings>
                <o:PixelsPerInch>96</o:PixelsPerInch>
            </o:OfficeDocumentSettings>
        </xml>
    </noscript>
    <![endif]-->
    <style type="text/css">
        /* Reset styles for email clients */
        body, table, td, p, a, li, blockquote {
            -webkit-text-size-adjust: 100%;
            -ms-text-size-adjust: 100%;
        }
        table, td {
            mso-table-lspace: 0pt;
            mso-table-rspace: 0pt;
        }
        img {
            -ms-interpolation-mode: bicubic;
            border: 0;
            height: auto;
            line-height: 100%;
            outline: none;
            text-decoration: none;
        }
        /* Outlook-specific styles */
        .ReadMsgBody {
            width: 100%;
        }
        .ExternalClass {
            width: 100%;
        }
        .ExternalClass, .ExternalClass p, .ExternalClass span, .ExternalClass font, .ExternalClass td, .ExternalClass div {
            line-height: 100%;
        }
        /* Prevent WebKit and Windows mobile changing default text sizes */
        table, td {
            mso-table-lspace: 0pt;
            mso-table-rspace: 0pt;
        }
        img {
            -ms-interpolation-mode: bicubic;
        }
        /* Responsive design */
        @media only screen and (max-width: 600px) {
            .mobile-full {
                width: 100% !important;
                height: auto !important;
            }
            .mobile-padding {
                padding: 10px !important;
            }
            /* Stack two-column rows on mobile */
            .stack td {
                display: block !important;
                width: 100% !important;
                box-sizing: border-box !important;
            }
        }
    </style>
</head>
<body style="margin: 0; padding: 0; background-color: ${settings.bodyBackgroundColor}; font-family: ${getFontStack(settings.templateFontFamily)}; font-size: ${settings.bodyFontSize}pt; color: ${settings.bodyTextColor}; line-height: ${settings.paragraphLineHeight};">
    <!--[if mso]>
    <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%">
    <tr>
    <td align="center">
    <![endif]-->
    
    <table data-kit-main-table="true" role="presentation" border="0" cellpadding="0" cellspacing="0" width="600" style="width: 100%; max-width: 600px; margin: ${settings.includeOuterBorder ? '20px auto' : '0 auto'}; background: white; border-radius: ${settings.tableBorderRadius}px; overflow: hidden; border: ${settings.includeOuterBorder ? `${settings.lineWidth || 1}px solid ${settings.outerBorderColor || '#e5e7eb'}` : 'none'}; box-shadow: 0 4px 12px rgba(0,0,0,0.05); mso-table-lspace:0pt; mso-table-rspace:0pt;">
        ${topBannersBefore}
        ${headerAreaHtml}
        ${topBannersAfter}
        ${generateMainContentHtml()}
        ${bottomBannersBefore}
        ${footerHtml}
        ${bottomBannersAfter}
    </table>
    
    <!--[if mso]>
    </td>
    </tr>
    </table>
    <![endif]-->
</body>
</html>`;

  return html;
}

const GenerateSection = ({
  settings,
  onNavigate,
  showNotification,
  onUpdate,
}) => {
  const { user } = useAuth();
  const [globalSettings, setGlobalSettings] = useState(null);
  const [copyStatus, setCopyStatus] = useState('Copy');
  const [groovyCopyStatus, setGroovyCopyStatus] = useState('Copy');
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [preview, setPreview] = useState('desktop');
  const [isSelectedEventArchived, setIsSelectedEventArchived] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testEmailSubject, setTestEmailSubject] = useState('');
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);
  const [testEmailStatus, setTestEmailStatus] = useState(null);
  const isMobileGeneratedPreview = preview === 'mobile';

  useEffect(() => {
    const fetchGlobalSettings = async () => {
      try {
        const data = await api.getGlobalSettings();
        setGlobalSettings(data);
      } catch (error) {
        console.warn('Failed to load global settings; using local fallback.', error);
        setGlobalSettings({
          placeholders: {},
          defaults: {},
          imageUrls: {},
        });
      }
    };
    fetchGlobalSettings();
  }, [user?.id]);

  useEffect(() => {
    (async () => {
      try {
        const evts = await api.getEvents();
        const selectedEventId = readSelectedEventScope(user?.id, null);
        if (selectedEventId && selectedEventId !== 'all') {
          const evt = evts.find(e => String(e.id) === String(selectedEventId));
          setIsSelectedEventArchived(!!(evt && evt.status === 'archived'));
        } else {
          setIsSelectedEventArchived(false);
        }
      } catch {}
    })();
  }, [user?.id]);

  const explicitRegmailSetting = useMemo(
    () =>
      settings &&
      typeof settings === 'object' &&
      Object.prototype.hasOwnProperty.call(settings, 'regmailEnabled')
        ? Boolean(settings.regmailEnabled)
        : undefined,
    [settings]
  );

  const regmailHeuristicsForTemplate = useMemo(() => {
    const templateName =
      settings?.name && typeof settings.name === 'string'
        ? settings.name.toLowerCase()
        : '';
    const categoryName =
      settings?.category && typeof settings.category === 'string'
        ? settings.category.toLowerCase()
        : '';
    if (templateName.includes('regmail') || categoryName.includes('regmail')) {
      return true;
    }

    const regmailKeys = [
      'eventName',
      'hostingOrganisation',
      'eventLocation',
      'eventStartDate',
      'eventEndDate',
      'eventTime',
      'paymentDueDate',
      'eventEmail',
    ];
    const hasKey = key =>
      settings && Object.prototype.hasOwnProperty.call(settings, key);
    if (regmailKeys.some(hasKey)) return true;

    const regmailBlock =
      settings?.regmail && typeof settings.regmail === 'object'
        ? settings.regmail
        : null;
    if (regmailBlock) {
      return regmailKeys.some(key =>
        Object.prototype.hasOwnProperty.call(regmailBlock, key)
      );
    }

    return false;
  }, [settings]);

  const isRegmailTemplate =
    explicitRegmailSetting ?? regmailHeuristicsForTemplate;

  const generatedHtml = useMemo(() => {
    if (!settings || !globalSettings) {
      return '';
    }

    return generateEmailHtml(settings, globalSettings);
  }, [settings, globalSettings]);

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedHtml.trim()).then(() => {
      setCopyStatus('Copied!');
      setTimeout(() => setCopyStatus('Copy'), 2000);
    });
  };

  const handleDownload = () => {
    const blob = new Blob([generatedHtml.trim()], { type: 'text/html' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${settings.name || 'email-template'}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  // Google Wallet Groovy snippet (shown when wallet buttons are enabled)
  const [walletGroovyCopyStatus, setWalletGroovyCopyStatus] = useState('Copy');
  const walletGroovyLine =
    'googlePassLink = lookupService.getParticipantMgr().buildGooglePass(p.id);';
  const handleCopyWalletGroovy = () => {
    navigator.clipboard.writeText(walletGroovyLine).then(() => {
      setWalletGroovyCopyStatus('Copied!');
      setTimeout(() => setWalletGroovyCopyStatus('Copy'), 2000);
    });
  };

  const defaultTestSubject = useMemo(() => {
    const descriptor =
      settings?.eventName ||
      settings?.name ||
      settings?.subject ||
      'Email Preview';
    return `Test Email: ${descriptor}`;
  }, [settings?.eventName, settings?.name, settings?.subject]);

  const handleSendTestEmail = async () => {
    const trimmedEmail = (testEmailAddress || '').trim();
    if (!trimmedEmail) {
      setTestEmailStatus({
        type: 'error',
        message: 'Please enter a recipient email address.',
      });
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(trimmedEmail)) {
      setTestEmailStatus({
        type: 'error',
        message: 'The recipient email address looks invalid.',
      });
      return;
    }

    if (!generatedHtml || !generatedHtml.trim()) {
      setTestEmailStatus({
        type: 'error',
        message: 'Generate the email first before sending a test message.',
      });
      return;
    }

    setIsSendingTestEmail(true);
    setTestEmailStatus(null);

    const subjectToUse =
      (testEmailSubject || '').trim() || defaultTestSubject;

    try {
      await api.sendTestEmail({
        to: trimmedEmail,
        subject: subjectToUse,
        html: generatedHtml,
      });
      setTestEmailStatus({
        type: 'success',
        message: `Sent a test email to ${trimmedEmail}.`,
      });
      if (typeof showNotification === 'function') {
        showNotification('Test email sent successfully.', 'success');
      }
    } catch (error) {
      setTestEmailStatus({
        type: 'error',
        message: error?.message || 'Failed to send test email.',
      });
      if (typeof showNotification === 'function') {
        showNotification(
          error?.message || 'Failed to send test email.',
          'error'
        );
      }
    } finally {
      setIsSendingTestEmail(false);
    }
  };

  const groovyScript = `// define reference for bank transfer
// not necessary to change
reference = event.mnemonic + ', ' + p.number + ', ' + p.lastName;
if (event.customerAccountNo != null) {
  reference = event.customerAccountNo + ', ' + reference;
}


/*
 * DO NOT TOUCH FROM HERE
 */


// determine payment type
paymentType = "";
if (p.preferredHotelPaymentType != null) {
  paymentType = p.preferredHotelPaymentType.name();
}
// possible values of paymentType: "", ONLINE_PAYMENT" and "ONLINE_PAYMENT"`;

  const handleCopyGroovy = () => {
    navigator.clipboard.writeText(groovyScript).then(() => {
      setGroovyCopyStatus('Copied!');
      setTimeout(() => setGroovyCopyStatus('Copy'), 2000);
    });
  };

  const handleSaveTemplate = async () => {
    const templateName = prompt(
      'Enter a name for this template:',
      settings.name || 'My Custom Template'
    );
    if (templateName) {
      setIsSaving(true);
      try {
        // Clean the settings object to remove any problematic data
        const cleanSettings = { ...settings };

        const regmailKeys = [
          'eventName',
          'hostingOrganisation',
          'eventLocation',
          'eventStartDate',
          'eventEndDate',
          'eventTime',
          'paymentDueDate',
          'eventEmail',
        ];
        const hasRegmailData = regmailKeys.some(key =>
          Object.prototype.hasOwnProperty.call(settings, key)
        );
        const explicitRegmailFlag =
          typeof settings.regmailEnabled === 'boolean'
            ? settings.regmailEnabled
            : undefined;
        const regmailEnabledForSave =
          explicitRegmailFlag !== undefined
            ? explicitRegmailFlag
            : hasRegmailData;
        cleanSettings.regmailEnabled = regmailEnabledForSave;

        // Remove any properties that might cause issues
        delete cleanSettings.id; // Remove ID to ensure it's treated as a new template
        delete cleanSettings.ownerId; // Remove ownerId as it will be set by the backend
        delete cleanSettings.visibility; // Remove visibility as it will be set to 'user'

        // Ensure this is saved as a user template, not global
        const selectedEventId = readSelectedEventScope(user?.id, null);
        const event_id =
          selectedEventId && selectedEventId !== 'all'
            ? parseInt(selectedEventId)
            : undefined;
        const templateData = {
          ...cleanSettings,
          name: templateName,
          visibility: 'user', // Force user visibility
          owner_id: user.id,
          category: regmailEnabledForSave ? 'Regmail' : null,
          ...(event_id ? { event_id } : {}),
        };

        const result = await api.saveTemplate(user.id, templateData);

        if (result.success) {
          showNotification(
            `Template "${templateName}" saved successfully!`,
            'success'
          );
          // Navigate back to template selection to show the newly saved template
          onNavigate('templateSelect');
        } else {
          showNotification(
            result.message || 'Failed to save template',
            'error'
          );
        }
      } catch (error) {
        console.error('Error saving template:', error);
        showNotification('Error saving template', 'error');
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleUpdateTemplate = async () => {
    if (!settings.id || settings.id === 'none') {
      showNotification('No template selected to update.', 'error');
      return;
    }
    // Allow updating template content without forcing ownership/visibility changes
    setIsUpdating(true);
    try {
      // Preserve existing owner, visibility, and event assignment
      const { id, ...rest } = settings;
      const payload = { id, ...rest };
      const result = await api.saveTemplate(user.id, payload);
      if (result.success) {
        showNotification('Template updated successfully!', 'success');
      } else {
        showNotification(
          result.message || 'Failed to update template',
          'error'
        );
      }
    } catch (e) {
      console.error('Error updating template:', e);
      showNotification('Error updating template', 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteMyTemplate = async () => {
    if (
      settings.ownerId === user.id &&
      window.confirm(
        `Are you sure you want to delete your template "${settings.name}"?`
      )
    ) {
      await api.deleteUserTemplate(user.id, settings.id);
      showNotification('Template deleted.', 'success');
      const templates = await api.getTemplates(user);
      const standardTemplate = templates.global.find(
        t => t.id === 'standard-template-id' || t.visibility === 'global'
      );
      onUpdate(null, standardTemplate || {});
      onNavigate('templateSelect');
    }
  };

  if (!globalSettings || !settings || Object.keys(settings).length === 0) {
    return (
      <div className="flex items-center justify-center h-48">
        <p>Loading template resources...</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gray-800 rounded-lg overflow-hidden max-w-full">
        <div className="flex justify-between items-center bg-gray-700 p-2 px-4">
          <span className="font-mono text-sm text-gray-300">
            Generated HTML
          </span>
          <div className="flex gap-4">
            <button
              onClick={handleCopy}
              className="text-sm flex items-center gap-2 text-gray-300 hover:text-white"
            >
              <CopyIcon /> {copyStatus}
            </button>
            <button
              onClick={handleDownload}
              className="text-sm flex items-center gap-2 text-gray-300 hover:text-white"
            >
              <DownloadIcon /> Download
            </button>
          </div>
        </div>
        <pre className="p-4 text-xs text-gray-200 overflow-x-auto max-h-80 whitespace-pre-wrap break-all">
          {generatedHtml.trim()}
        </pre>
      </div>

      {settings.includeWalletButtons && (
        <div className="bg-gray-800 rounded-lg overflow-hidden max-w-full mt-3">
          <div className="flex justify-between items-center bg-gray-700 p-2 px-4">
            <span className="font-mono text-sm text-gray-300">
              Google Wallet Groovy Snippet
            </span>
            <div className="flex gap-4">
              <button
                onClick={handleCopyWalletGroovy}
                className="text-sm flex items-center gap-2 text-gray-300 hover:text-white"
              >
                <CopyIcon /> {walletGroovyCopyStatus}
              </button>
            </div>
          </div>
          <div className="p-3 text-xs text-gray-200 bg-gray-700">
            Copy this line into the script tab in the REACT email template to
            generate the Google Wallet link for each participant.
          </div>
          <pre className="p-4 text-xs text-gray-200 overflow-x-auto max-h-32 whitespace-pre-wrap break-all">
            {walletGroovyLine}
          </pre>
        </div>
      )}

      {isRegmailTemplate && (
        <div className="bg-gray-800 rounded-lg overflow-hidden max-w-full mt-3">
          <div className="flex justify-between items-center bg-gray-700 p-2 px-4">
            <span className="font-mono text-sm text-gray-300">
              Groovy Script (copy into Regmail)
            </span>
            <div className="flex gap-4">
              <button
                onClick={handleCopyGroovy}
                className="text-sm flex items-center gap-2 text-gray-300 hover:text-white"
              >
                <CopyIcon /> {groovyCopyStatus}
              </button>
            </div>
          </div>
          <pre className="p-4 text-xs text-gray-200 overflow-x-auto max-h-48 whitespace-pre-wrap break-all">
            {groovyScript}
          </pre>
        </div>
      )}

      <div className="mt-6 flex justify-end gap-4">
        {settings.ownerId === user.id && (
          <>
            <button
              onClick={handleDeleteMyTemplate}
              className="nav-button-secondary bg-red-100 text-red-700 hover:bg-red-200"
              disabled={isSelectedEventArchived}
              title={
                isSelectedEventArchived ? 'Selected event is archived' : ''
              }
            >
              Delete This Template
            </button>
            <button
              onClick={handleUpdateTemplate}
              disabled={isUpdating || isSelectedEventArchived}
              className="nav-button-secondary"
              title={
                isSelectedEventArchived ? 'Selected event is archived' : ''
              }
            >
              {isUpdating ? 'Updating...' : 'Update Template'}
            </button>
          </>
        )}
        <button
          onClick={handleSaveTemplate}
          disabled={isSaving || isSelectedEventArchived}
          className="nav-button-primary"
          title={isSelectedEventArchived ? 'Selected event is archived' : ''}
        >
          {isSaving ? 'Saving...' : 'Save as New Template'}
        </button>
      </div>

      <div className="flex justify-between items-center mt-8 mb-4">
        <h3 className="text-lg font-medium text-gray-900">Live Preview</h3>
        <PreviewToggle view={preview} setView={setPreview} />
      </div>

      <div className="glass-panel mt-4 p-5 space-y-4">
        <div>
          <h4 className="text-base font-semibold text-slate-900">
            Send a Test Email
          </h4>
          <p className="text-sm text-slate-600">
            Deliver this template to your inbox to review it in a real email
            client. Configure SMTP settings on the server to enable this feature.
          </p>
        </div>
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Recipient Email
            </label>
            <input
              type="email"
              value={testEmailAddress}
              onChange={event => {
                setTestEmailAddress(event.target.value);
                setTestEmailStatus(null);
              }}
              placeholder="name@example.com"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm transition focus:border-primary focus:outline-none focus:ring focus:ring-primary/20"
              autoComplete="email"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Subject (optional)
            </label>
            <input
              type="text"
              value={testEmailSubject}
              onChange={event => {
                setTestEmailSubject(event.target.value);
                setTestEmailStatus(null);
              }}
              placeholder={defaultTestSubject}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm transition focus:border-primary focus:outline-none focus:ring focus:ring-primary/20"
            />
          </div>
        </div>
        {testEmailStatus && (
          <div
            className={`rounded-lg border px-3 py-2 text-sm ${
              testEmailStatus.type === 'success'
                ? 'border-green-200 bg-green-50 text-green-700'
                : 'border-rose-200 bg-rose-50 text-rose-700'
            }`}
            role="status"
          >
            {testEmailStatus.message}
          </div>
        )}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSendTestEmail}
            className="nav-button-primary disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={
              isSendingTestEmail ||
              !(testEmailAddress || '').trim() ||
              !generatedHtml?.trim()
            }
          >
            {isSendingTestEmail ? 'Sending...' : 'Send Test Email'}
          </button>
        </div>
      </div>

      {(() => {
        const font = settings?.templateFontFamily;
        if (font && !systemFonts.has(font)) {
          return (
            <div className="text-xs text-gray-500 mt-2 mb-4 italic bg-gray-50 rounded px-3 py-2 max-w-lg">
              Note: Web fonts like <span className="font-semibold">{font}</span>{' '}
              are only supported in a few email clients (e.g. Apple Mail, iOS
              Mail, Outlook for Mac). Most clients will use the fallback font.
            </div>
          );
        }
        return null;
      })()}

      <div className="flex justify-center">
        <div
          className={`w-full ${
            isMobileGeneratedPreview ? 'max-w-[460px]' : 'max-w-[1100px]'
          } ${
            isMobileGeneratedPreview
              ? 'rounded-[32px] border border-slate-200 bg-gradient-to-br from-indigo-50 to-pink-50 p-4 shadow-2xl'
              : 'rounded-3xl border border-slate-200 bg-white p-4 shadow-xl'
          }`}
        >
          <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden">
            <iframe
              srcDoc={generatedHtml}
              title="Email Preview"
              className="w-full border-0"
              style={{ minHeight: `${isMobileGeneratedPreview ? 640 : 720}px` }}
            />
          </div>
        </div>
      </div>

      <div className="glass-panel mt-8 p-6 space-y-4">
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-slate-900">Accessibility Checker</h3>
          <p className="text-sm text-slate-600">Review colour contrast and heading structure in the dedicated accessibility section before exporting.</p>
        </div>
        <button
          type="button"
          onClick={() => onNavigate('accessibility')}
          className="inline-flex items-center rounded-lg border border-primary/40 bg-primary/10 px-4 py-2 text-sm font-semibold text-primary transition hover:border-primary hover:bg-primary/20"
        >
          Open Accessibility Checker
        </button>
      </div>

      <div className="text-center p-8 mt-6 bg-gray-50/80 rounded-lg">
        <CheckIcon />
        <h3 className="text-xl font-bold mt-4">Configuration Complete!</h3>
        <p className="text-gray-600 mt-2 max-w-md mx-auto">
          You can copy, download, or save the generated template. To make
          changes, go back to the previous sections.
        </p>
      </div>

      <div className="mt-10 flex justify-start">
        <NavButtons
          onPrev={() => onNavigate('accessibility')}
          onNext={() => onNavigate('templateSelect')}
          prevLabel="Previous: Accessibility"
          nextLabel="Back to Top"
        />
      </div>
    </>
  );
};
export default GenerateSection;
